/**
 * Elite Batch 16 Part 3 — Workflow Templates 1488–1507
 * Travel insurance, itinerary planning, weather planners, weekly reviews,
 * retrospectives, review monitors, web scraping, brand monitoring,
 * competitive pricing, sentiment analysis, data extraction, image analysis,
 * legal research, PR crisis, price comparison, product review aggregation.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 16 PART 3 — TEMPLATES 1488–1507
// =============================================================================

export const ELITE_BATCH16_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1488. TRAVEL INSURANCE ADVISOR BOT (8 nodes)
  // ===================================================================

  "Travel Insurance Advisor Bot": {
    summary:
      "Receives trip data via webhook, extracts travel details with AI, collects trip specifics via runCode, performs risk assessment, fetches coverage comparisons from an insurance API, recommends plans with AI, explains exclusion fine print with AI, and sends a quote purchase email.",
    nodes: [
      trigger("Webhook: Travel insurance inquiry", "webhook_app", {
        source: "travel_portal",
        event: "insurance.inquiry_submitted",
        howItStarts:
          "Fires when a traveler submits an insurance inquiry through the travel portal, including destination, dates, activities, and traveler demographics",
        setup:
          "Configure webhook from travel booking portal. Set OpenAI API key for AI analysis. Configure insurance comparison API credentials. Set email service for quote delivery.",
        inputFields: [],
      }),

      // 2. AI extract trip data
      aiNode(
        "2",
        "AI Extract Trip and Traveler Data",
        280,
        "You are a travel insurance specialist. Extract trip and traveler details from the raw webhook payload. Find destination, travel dates, number of travelers, ages, pre-existing conditions, planned activities, and budget preferences regardless of field naming. Respond JSON: {destination: string, departure_date: string, return_date: string, trip_duration_days: number, traveler_count: number, traveler_ages: string, pre_existing_conditions: string, planned_activities: string, budget_preference: string, traveler_email: string, traveler_name: string}",
        "Raw webhook payload: {webhook_payload}. Extract all travel insurance inquiry parameters regardless of payload structure.",
        [
          { field: "destination", as: "destination", type: "string" },
          { field: "departure_date", as: "departure_date", type: "string" },
          { field: "return_date", as: "return_date", type: "string" },
          {
            field: "trip_duration_days",
            as: "trip_duration_days",
            type: "number",
          },
          { field: "traveler_count", as: "traveler_count", type: "number" },
          { field: "traveler_ages", as: "traveler_ages", type: "string" },
          {
            field: "pre_existing_conditions",
            as: "pre_existing_conditions",
            type: "string",
          },
          {
            field: "planned_activities",
            as: "planned_activities",
            type: "string",
          },
          {
            field: "budget_preference",
            as: "budget_preference",
            type: "string",
          },
          { field: "traveler_email", as: "traveler_email", type: "string" },
          { field: "traveler_name", as: "traveler_name", type: "string" },
        ],
      ),

      // 3. Trip details collector runCode
      n("3", "runCode", "Compile Trip Risk Profile", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build a comprehensive trip risk profile for insurance assessment
const destination = vars.destination || "Unknown";
const activities = (vars.planned_activities || "sightseeing").toLowerCase();
const ages = (vars.traveler_ages || "30").split(",").map(a => parseInt(a.trim(), 10));
const maxAge = Math.max(...ages);
const minAge = Math.min(...ages);
const conditions = vars.pre_existing_conditions || "none";
const duration = parseInt(vars.trip_duration_days || "7", 10);

// Risk factor calculations
const highRiskActivities = ["skiing", "scuba", "skydiving", "bungee", "rafting", "climbing", "surfing", "paragliding"];
const activityRiskItems = highRiskActivities.filter(a => activities.includes(a));
const activityRiskLevel = activityRiskItems.length > 2 ? "high" : activityRiskItems.length > 0 ? "medium" : "low";
const ageRiskLevel = maxAge > 70 || minAge < 2 ? "high" : maxAge > 55 ? "medium" : "low";
const medicalRiskLevel = conditions !== "none" && conditions !== "" ? "elevated" : "standard";
const durationRiskLevel = duration > 30 ? "extended" : duration > 14 ? "moderate" : "standard";
const overallRisk = activityRiskLevel === "high" || ageRiskLevel === "high" ? "high" : activityRiskLevel === "medium" || ageRiskLevel === "medium" ? "medium" : "low";

return {
  risk_profile_summary: destination + " | " + duration + " days | " + vars.traveler_count + " travelers | Age range: " + minAge + "-" + maxAge,
  activity_risk_level: activityRiskLevel,
  activity_risk_items: activityRiskItems.join(", ") || "none",
  age_risk_level: ageRiskLevel,
  medical_risk_level: medicalRiskLevel,
  duration_risk_level: durationRiskLevel,
  overall_risk_score: overallRisk,
  requires_medical_declaration: conditions !== "none" && conditions !== "" ? "yes" : "no"
};`,
          outputVariables: [
            { name: "risk_profile_summary", type: "string" },
            { name: "activity_risk_level", type: "string" },
            { name: "activity_risk_items", type: "string" },
            { name: "age_risk_level", type: "string" },
            { name: "medical_risk_level", type: "string" },
            { name: "duration_risk_level", type: "string" },
            { name: "overall_risk_score", type: "string" },
            { name: "requires_medical_declaration", type: "string" },
          ],
        },
      }),

      // 4. Risk assessment engine runCode
      n("4", "runCode", "Calculate Premium Estimates by Tier", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate insurance premium estimates across tiers
const travelers = parseInt(vars.traveler_count || "1", 10);
const days = parseInt(vars.trip_duration_days || "7", 10);
const risk = vars.overall_risk_score || "low";
const riskMultipliers = { low: 1.0, medium: 1.35, high: 1.75 };
const multiplier = riskMultipliers[risk] || 1.0;
const baseDailyRate = 4.50;

const basicDaily = baseDailyRate * multiplier;
const standardDaily = baseDailyRate * 2.2 * multiplier;
const premiumDaily = baseDailyRate * 3.8 * multiplier;

const basicTotal = Math.round(basicDaily * days * travelers * 100) / 100;
const standardTotal = Math.round(standardDaily * days * travelers * 100) / 100;
const premiumTotal = Math.round(premiumDaily * days * travelers * 100) / 100;

return {
  basic_plan_total: basicTotal,
  basic_plan_coverage: "Medical: $50K, Cancellation: $2K, Baggage: $500",
  standard_plan_total: standardTotal,
  standard_plan_coverage: "Medical: $250K, Cancellation: $10K, Baggage: $2K, Delay: $1K",
  premium_plan_total: premiumTotal,
  premium_plan_coverage: "Medical: $1M, Cancellation: $25K, Baggage: $5K, Delay: $2K, Adventure Sports, Evacuation",
  recommended_tier: risk === "high" ? "premium" : risk === "medium" ? "standard" : "standard",
  price_per_day_basic: Math.round(basicDaily * 100) / 100,
  price_per_day_premium: Math.round(premiumDaily * 100) / 100
};`,
          outputVariables: [
            { name: "basic_plan_total", type: "number" },
            { name: "basic_plan_coverage", type: "string" },
            { name: "standard_plan_total", type: "number" },
            { name: "standard_plan_coverage", type: "string" },
            { name: "premium_plan_total", type: "number" },
            { name: "premium_plan_coverage", type: "string" },
            { name: "recommended_tier", type: "string" },
            { name: "price_per_day_basic", type: "number" },
            { name: "price_per_day_premium", type: "number" },
          ],
        },
      }),

      // 5. Coverage comparison API
      n("5", "hitApi", "Fetch Live Coverage Comparisons from Insurers", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.comparetravelinsurance.com/v2/quotes",
          headers: [
            { key: "Authorization", value: "Bearer {insurance_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "destination", value: "{destination}" },
            { key: "departure_date", value: "{departure_date}" },
            { key: "return_date", value: "{return_date}" },
            { key: "travelers", value: "{traveler_count}" },
            { key: "ages", value: "{traveler_ages}" },
            { key: "activities", value: "{planned_activities}" },
            { key: "risk_level", value: "{overall_risk_score}" },
          ],
          variables: [
            { name: "quotes", customName: "insurer_quotes", type: "string" },
            {
              name: "best_value.provider",
              customName: "best_value_provider",
              type: "string",
            },
            {
              name: "best_value.price",
              customName: "best_value_price",
              type: "number",
            },
            {
              name: "total_quotes",
              customName: "quotes_returned",
              type: "number",
            },
          ],
        },
      }),

      // 6. AI plan recommender
      aiNode(
        "6",
        "AI Recommend Best Insurance Plan",
        1240,
        "You are a licensed travel insurance advisor. Based on the trip risk profile, premium estimates, and live insurer quotes, recommend the best plan for this traveler. Explain why the recommended plan fits their specific needs. Consider activity risks, medical conditions, and budget. Respond JSON: {recommended_plan: string, recommendation_reason: string, key_benefits: string, value_rating: string, alternative_plan: string, savings_tip: string}",
        "Traveler: {traveler_name}. Trip: {risk_profile_summary}. Risk: {overall_risk_score}. Activities risk: {activity_risk_level} ({activity_risk_items}). Medical: {medical_risk_level}. Pre-existing: {pre_existing_conditions}. Budget: {budget_preference}. Basic: ${basic_plan_total} ({basic_plan_coverage}). Standard: ${standard_plan_total} ({standard_plan_coverage}). Premium: ${premium_plan_total} ({premium_plan_coverage}). Best value insurer: {best_value_provider} at ${best_value_price}. Quotes found: {quotes_returned}.",
        [
          { field: "recommended_plan", as: "recommended_plan", type: "string" },
          {
            field: "recommendation_reason",
            as: "recommendation_reason",
            type: "string",
          },
          { field: "key_benefits", as: "key_benefits", type: "string" },
          { field: "value_rating", as: "value_rating", type: "string" },
          { field: "alternative_plan", as: "alternative_plan", type: "string" },
          { field: "savings_tip", as: "savings_tip", type: "string" },
        ],
      ),

      // 7. Exclusion fine print explainer AI
      aiNode(
        "7",
        "AI Explain Exclusions and Fine Print",
        1480,
        "You are a consumer-friendly insurance advisor. Explain the key exclusions, limitations, and fine print of the recommended plan in plain language. Highlight what is NOT covered, especially related to the travelers specific activities and conditions. Warn about common claim denial reasons. Respond JSON: {exclusion_summary: string, activity_exclusions: string, medical_exclusions: string, claim_tips: string, common_pitfalls: string}",
        "Recommended plan: {recommended_plan}. Coverage: {key_benefits}. Activities: {planned_activities}. Activity risk items: {activity_risk_items}. Pre-existing conditions: {pre_existing_conditions}. Medical risk: {medical_risk_level}. Destination: {destination}. Trip duration: {trip_duration_days} days. Explain exclusions clearly.",
        [
          {
            field: "exclusion_summary",
            as: "exclusion_summary",
            type: "string",
          },
          {
            field: "activity_exclusions",
            as: "activity_exclusions",
            type: "string",
          },
          {
            field: "medical_exclusions",
            as: "medical_exclusions",
            type: "string",
          },
          { field: "claim_tips", as: "claim_tips", type: "string" },
          { field: "common_pitfalls", as: "common_pitfalls", type: "string" },
        ],
      ),

      // 8. Quote purchase email
      emailNode(
        "8",
        "Send Insurance Quote and Purchase Link",
        1720,
        "{traveler_email}",
        "Your Travel Insurance Quote for {destination} — {recommended_plan} Recommended",
        "<h2>Travel Insurance Quote for {traveler_name}</h2><p><strong>Trip:</strong> {destination} | {departure_date} to {return_date} ({trip_duration_days} days) | {traveler_count} travelers</p><h3>Our Recommendation: {recommended_plan}</h3><p>{recommendation_reason}</p><p><strong>Key Benefits:</strong> {key_benefits}</p><p><strong>Value Rating:</strong> {value_rating}</p><hr><h3>Plan Comparison</h3><table><tr><th>Plan</th><th>Total Price</th><th>Coverage</th></tr><tr><td>Basic</td><td>${basic_plan_total}</td><td>{basic_plan_coverage}</td></tr><tr><td>Standard</td><td>${standard_plan_total}</td><td>{standard_plan_coverage}</td></tr><tr><td>Premium</td><td>${premium_plan_total}</td><td>{premium_plan_coverage}</td></tr></table><p><strong>Best Value Provider:</strong> {best_value_provider} at ${best_value_price}</p><hr><h3>Important Exclusions</h3><p>{exclusion_summary}</p><p><strong>Activity Exclusions:</strong> {activity_exclusions}</p><p><strong>Medical Exclusions:</strong> {medical_exclusions}</p><p><strong>Claim Tips:</strong> {claim_tips}</p><p><strong>Common Pitfalls:</strong> {common_pitfalls}</p><hr><p><strong>Savings Tip:</strong> {savings_tip}</p><p><strong>Alternative:</strong> {alternative_plan}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1489. TRAVEL ITINERARY ORGANIZER (8 nodes)
  // ===================================================================

  "Travel Itinerary Organizer": {
    summary:
      "Receives travel documents via webhook, extracts itinerary data with AI, builds a structured itinerary with runCode, detects gaps and conflicts with a condition, suggests activities with AI for gap-filling, syncs events to Google Calendar, generates an itinerary document with runCode, and monitors real-time updates in Sheets.",
    nodes: [
      trigger("Webhook: Travel document received", "webhook_app", {
        source: "email_parser",
        event: "travel.document_received",
        howItStarts:
          "Fires when a travel confirmation email or booking document is forwarded to the system, containing flight, hotel, or activity booking details in any format",
        setup:
          "Configure email forwarding or document upload webhook. Set OpenAI API key for AI extraction. Configure Google Calendar API for event sync. Set Google Sheets for itinerary tracking.",
        inputFields: [],
      }),

      // 2. AI itinerary extractor
      aiNode(
        "2",
        "AI Extract Itinerary Details from Document",
        280,
        "You are a travel document parser. Extract all itinerary items from the raw webhook payload which may contain flight confirmations, hotel bookings, activity reservations, or car rentals. Find dates, times, locations, confirmation numbers, and costs regardless of document format. Respond JSON: {trip_name: string, items: string, start_date: string, end_date: string, total_items: number, destinations: string, traveler_email: string, total_cost_estimate: number}",
        "Raw webhook payload: {webhook_payload}. Parse all travel booking details from this document regardless of format — could be email, PDF text, or structured data.",
        [
          { field: "trip_name", as: "trip_name", type: "string" },
          { field: "items", as: "itinerary_items_json", type: "string" },
          { field: "start_date", as: "trip_start_date", type: "string" },
          { field: "end_date", as: "trip_end_date", type: "string" },
          { field: "total_items", as: "total_items", type: "number" },
          { field: "destinations", as: "destinations", type: "string" },
          { field: "traveler_email", as: "traveler_email", type: "string" },
          {
            field: "total_cost_estimate",
            as: "total_cost_estimate",
            type: "number",
          },
        ],
      ),

      // 3. Itinerary builder runCode
      n("3", "runCode", "Build Structured Day-by-Day Itinerary", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build a structured day-by-day itinerary from extracted items
const items = JSON.parse(vars.itinerary_items_json || "[]");
const startDate = new Date(vars.trip_start_date || Date.now());
const endDate = new Date(vars.trip_end_date || Date.now());
const tripDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

// Group items by date
const dayMap = {};
for (let d = 0; d < tripDays; d++) {
  const date = new Date(startDate);
  date.setDate(date.getDate() + d);
  const key = date.toISOString().split("T")[0];
  dayMap[key] = [];
}
items.forEach(item => {
  const itemDate = (item.date || item.start_date || "").split("T")[0];
  if (dayMap[itemDate]) dayMap[itemDate].push(item);
  else if (Object.keys(dayMap).length > 0) dayMap[Object.keys(dayMap)[0]].push(item);
});

// Detect gaps (days with no activities)
const gapDays = Object.entries(dayMap).filter(([_, events]) => events.length === 0).map(([date]) => date);
const hasGaps = gapDays.length > 0 ? "yes" : "no";

// Detect time conflicts (overlapping events on same day)
let conflicts = [];
Object.entries(dayMap).forEach(([date, events]) => {
  if (events.length > 1) {
    for (let i = 0; i < events.length - 1; i++) {
      for (let j = i + 1; j < events.length; j++) {
        if (events[i].start_time && events[j].start_time && events[i].end_time > events[j].start_time) {
          conflicts.push(date + ": " + (events[i].name || "Event") + " overlaps " + (events[j].name || "Event"));
        }
      }
    }
  }
});

return {
  structured_itinerary: JSON.stringify(dayMap),
  trip_days: tripDays,
  gap_days: gapDays.join(", ") || "none",
  has_gaps_or_conflicts: hasGaps === "yes" || conflicts.length > 0 ? "yes" : "no",
  conflict_details: conflicts.join("; ") || "none",
  gap_count: gapDays.length,
  filled_days: tripDays - gapDays.length
};`,
          outputVariables: [
            { name: "structured_itinerary", type: "string" },
            { name: "trip_days", type: "number" },
            { name: "gap_days", type: "string" },
            { name: "has_gaps_or_conflicts", type: "string" },
            { name: "conflict_details", type: "string" },
            { name: "gap_count", type: "number" },
            { name: "filled_days", type: "number" },
          ],
        },
      }),

      // 4. Gap conflict detector condition
      n("4", "condition", "Gaps or Conflicts Detected?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "has_gaps_or_conflicts", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 5. AI activity suggester (true path — gaps exist)
      aiNode(
        "5",
        "AI Suggest Activities for Gap Days",
        1000,
        "You are a local travel expert. For each gap day in the itinerary, suggest 2-3 activities appropriate for the destination. Consider the existing itinerary flow, traveler preferences, and local highlights. For conflicts, suggest resolution options. Respond JSON: {suggested_activities: string, gap_fill_plan: string, conflict_resolutions: string, enhanced_itinerary_notes: string}",
        "Trip: {trip_name}. Destinations: {destinations}. Gap days: {gap_days}. Conflicts: {conflict_details}. Current itinerary: {structured_itinerary}. Suggest activities to fill gaps and resolve conflicts.",
        [
          {
            field: "suggested_activities",
            as: "suggested_activities",
            type: "string",
          },
          { field: "gap_fill_plan", as: "gap_fill_plan", type: "string" },
          {
            field: "conflict_resolutions",
            as: "conflict_resolutions",
            type: "string",
          },
          {
            field: "enhanced_itinerary_notes",
            as: "enhanced_itinerary_notes",
            type: "string",
          },
        ],
        80,
      ),

      // 6. Calendar sync Google Calendar API
      n("6", "hitApi", "Sync Itinerary Events to Google Calendar", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/{calendar_id}/events",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "summary", value: "{trip_name} — Itinerary" },
            {
              key: "description",
              value:
                "Auto-synced itinerary: {total_items} events across {trip_days} days. Destinations: {destinations}. Gaps filled: {gap_count}. Notes: {enhanced_itinerary_notes}",
            },
            { key: "start", value: '{"date":"{trip_start_date}"}' },
            { key: "end", value: '{"date":"{trip_end_date}"}' },
          ],
          variables: [
            { name: "id", customName: "calendar_event_id", type: "string" },
            { name: "htmlLink", customName: "calendar_link", type: "string" },
            { name: "status", customName: "sync_status", type: "string" },
          ],
        },
      }),

      // 7. Itinerary document generator runCode
      n("7", "runCode", "Generate Printable Itinerary Document", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a formatted itinerary document
const itinerary = JSON.parse(vars.structured_itinerary || "{}");
const tripName = vars.trip_name || "My Trip";
const destinations = vars.destinations || "Unknown";
const suggestions = vars.suggested_activities || "";
const gapPlan = vars.gap_fill_plan || "";

let doc = "# " + tripName + "\\n";
doc += "Destinations: " + destinations + "\\n";
doc += "Dates: " + vars.trip_start_date + " to " + vars.trip_end_date + " (" + vars.trip_days + " days)\\n";
doc += "Total Budget: $" + vars.total_cost_estimate + "\\n";
doc += "Calendar: " + (vars.calendar_link || "synced") + "\\n\\n";

Object.entries(itinerary).forEach(([date, events]) => {
  doc += "## " + date + "\\n";
  if (events.length === 0) {
    doc += "  Free day — " + (gapPlan || "explore at leisure") + "\\n";
  } else {
    events.forEach(ev => {
      doc += "  - " + (ev.time || "") + " " + (ev.name || ev.type || "Activity") + " at " + (ev.location || "TBD") + "\\n";
    });
  }
});

if (suggestions) doc += "\\n## Suggested Activities\\n" + suggestions + "\\n";

return {
  itinerary_document: doc,
  document_length: doc.length,
  generation_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "itinerary_document", type: "string" },
            { name: "document_length", type: "number" },
            { name: "generation_timestamp", type: "string" },
          ],
        },
      }),

      // 8. Real-time update monitor Sheets
      sheetsNode(
        "8",
        "Log Itinerary to Sheets for Updates",
        1720,
        "TripItineraries",
        '[["{generation_timestamp}","{trip_name}","{destinations}","{trip_start_date}","{trip_end_date}","{trip_days}","{total_items}","{gap_count}","{total_cost_estimate}","{sync_status}","{calendar_link}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1490. TRIP PLANNING ASSISTANT BOT (9 nodes)
  // ===================================================================

  "Trip Planning Assistant Bot": {
    summary:
      "Receives a travel brief via webhook, extracts travel preferences with AI, generates travel advice with AI, searches flights via API, searches accommodations via API, finds activities via API, builds a complete itinerary with AI, calculates budget with runCode, and sends a trip summary email.",
    nodes: [
      trigger("Webhook: Trip planning request", "webhook_app", {
        source: "travel_assistant",
        event: "trip.planning_requested",
        howItStarts:
          "Fires when a user submits a trip planning request with destination preferences, dates, budget, interests, and group size",
        setup:
          "Configure webhook from trip planning form or chat interface. Set OpenAI API key for AI planning. Configure flight, hotel, and activity search APIs. Set email service for trip summary delivery.",
        inputFields: [],
      }),

      // 2. AI extract travel data
      aiNode(
        "2",
        "AI Extract Travel Preferences",
        280,
        "You are a travel planning assistant. Extract all trip preferences from the raw webhook payload. Find destination(s), travel dates, budget, group size, interests, accommodation preferences, dietary restrictions, and mobility requirements regardless of format. Respond JSON: {destination: string, alt_destinations: string, start_date: string, end_date: string, budget_total: number, budget_currency: string, group_size: number, interests: string, accommodation_type: string, traveler_email: string, special_requirements: string}",
        "Raw webhook payload: {webhook_payload}. Extract all trip planning preferences regardless of payload structure.",
        [
          { field: "destination", as: "destination", type: "string" },
          { field: "alt_destinations", as: "alt_destinations", type: "string" },
          { field: "start_date", as: "start_date", type: "string" },
          { field: "end_date", as: "end_date", type: "string" },
          { field: "budget_total", as: "budget_total", type: "number" },
          { field: "budget_currency", as: "budget_currency", type: "string" },
          { field: "group_size", as: "group_size", type: "number" },
          { field: "interests", as: "interests", type: "string" },
          {
            field: "accommodation_type",
            as: "accommodation_type",
            type: "string",
          },
          { field: "traveler_email", as: "traveler_email", type: "string" },
          {
            field: "special_requirements",
            as: "special_requirements",
            type: "string",
          },
        ],
      ),

      // 3. AI travel advisor
      aiNode(
        "3",
        "AI Generate Travel Advice and Tips",
        520,
        "You are an experienced travel advisor. Based on the traveler preferences, provide destination-specific advice including best neighborhoods to stay, local customs, safety tips, best time to visit attractions, money-saving tips, and must-try local experiences. Respond JSON: {destination_tips: string, best_neighborhoods: string, local_customs: string, safety_notes: string, money_tips: string, must_try_experiences: string}",
        "Destination: {destination}. Dates: {start_date} to {end_date}. Group size: {group_size}. Interests: {interests}. Budget: {budget_total} {budget_currency}. Accommodation preference: {accommodation_type}. Special requirements: {special_requirements}.",
        [
          { field: "destination_tips", as: "destination_tips", type: "string" },
          {
            field: "best_neighborhoods",
            as: "best_neighborhoods",
            type: "string",
          },
          { field: "local_customs", as: "local_customs", type: "string" },
          { field: "safety_notes", as: "safety_notes", type: "string" },
          { field: "money_tips", as: "money_tips", type: "string" },
          {
            field: "must_try_experiences",
            as: "must_try_experiences",
            type: "string",
          },
        ],
      ),

      // 4. Flight search API
      n("4", "hitApi", "Search Flights via Skyscanner API", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://partners.api.skyscanner.net/apiservices/v3/flights/live/search/create",
          headers: [
            { key: "x-api-key", value: "{skyscanner_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "query.market", value: "US" },
            { key: "query.locale", value: "en-US" },
            { key: "query.currency", value: "{budget_currency}" },
            {
              key: "query.dateTimeGroupingType",
              value: "DATE_TIME_GROUPING_TYPE_BY_DATE",
            },
            { key: "query.destination", value: "{destination}" },
            { key: "query.departDate", value: "{start_date}" },
            { key: "query.returnDate", value: "{end_date}" },
            { key: "query.adults", value: "{group_size}" },
          ],
          variables: [
            {
              name: "content.results.itineraries",
              customName: "flight_options",
              type: "string",
            },
            {
              name: "content.sortingOptions.best[0].price",
              customName: "best_flight_price",
              type: "string",
            },
            {
              name: "content.sortingOptions.cheapest[0].price",
              customName: "cheapest_flight_price",
              type: "string",
            },
            {
              name: "content.stats.total",
              customName: "flight_results_count",
              type: "number",
            },
          ],
        },
      }),

      // 5. Accommodation search API
      n("5", "hitApi", "Search Accommodations via Booking API", 1000, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.booking.com/v2/hotels",
          headers: [
            { key: "Authorization", value: "Bearer {booking_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "destination", value: "{destination}" },
            { key: "checkin", value: "{start_date}" },
            { key: "checkout", value: "{end_date}" },
            { key: "guests", value: "{group_size}" },
            { key: "accommodation_type", value: "{accommodation_type}" },
            { key: "currency", value: "{budget_currency}" },
            { key: "sort_by", value: "review_score" },
          ],
          variables: [
            {
              name: "results",
              customName: "accommodation_options",
              type: "string",
            },
            {
              name: "results[0].name",
              customName: "top_hotel_name",
              type: "string",
            },
            {
              name: "results[0].price_per_night",
              customName: "top_hotel_price",
              type: "number",
            },
            {
              name: "results[0].review_score",
              customName: "top_hotel_rating",
              type: "number",
            },
            {
              name: "total_results",
              customName: "accommodation_results_count",
              type: "number",
            },
          ],
        },
      }),

      // 6. Activity finder API
      n("6", "hitApi", "Search Local Activities via GetYourGuide API", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.getyourguide.com/1/activities",
          headers: [
            { key: "X-Access-Token", value: "{getyourguide_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "q", value: "{destination} {interests}" },
            { key: "date_from", value: "{start_date}" },
            { key: "date_to", value: "{end_date}" },
            { key: "participants", value: "{group_size}" },
            { key: "currency", value: "{budget_currency}" },
            { key: "sort_by", value: "popularity" },
          ],
          variables: [
            {
              name: "data.activities",
              customName: "activity_options",
              type: "string",
            },
            {
              name: "data.activities[0].title",
              customName: "top_activity_name",
              type: "string",
            },
            {
              name: "data.activities[0].price.amount",
              customName: "top_activity_price",
              type: "number",
            },
            {
              name: "data.total",
              customName: "activity_results_count",
              type: "number",
            },
          ],
        },
      }),

      // 7. AI itinerary planner
      aiNode(
        "7",
        "AI Build Complete Day-by-Day Itinerary",
        1480,
        "You are an expert trip planner. Using the flight options, accommodation options, and activity options, create a detailed day-by-day itinerary that fits within the budget. Include specific timings, locations, travel tips for each day, and meal recommendations. Respond JSON: {full_itinerary: string, highlight_experiences: string, daily_breakdown: string, transportation_plan: string, dining_recommendations: string, packing_suggestions: string}",
        "Destination: {destination}. Dates: {start_date} to {end_date}. Budget: {budget_total} {budget_currency}. Group: {group_size}. Interests: {interests}. Best flight: {best_flight_price}. Top hotel: {top_hotel_name} at {top_hotel_price}/night (rated {top_hotel_rating}). Top activity: {top_activity_name} at {top_activity_price}. Neighborhoods: {best_neighborhoods}. Must-try: {must_try_experiences}. Tips: {destination_tips}. Safety: {safety_notes}. Requirements: {special_requirements}.",
        [
          { field: "full_itinerary", as: "full_itinerary", type: "string" },
          {
            field: "highlight_experiences",
            as: "highlight_experiences",
            type: "string",
          },
          { field: "daily_breakdown", as: "daily_breakdown", type: "string" },
          {
            field: "transportation_plan",
            as: "transportation_plan",
            type: "string",
          },
          {
            field: "dining_recommendations",
            as: "dining_recommendations",
            type: "string",
          },
          {
            field: "packing_suggestions",
            as: "packing_suggestions",
            type: "string",
          },
        ],
      ),

      // 8. Budget calculator runCode
      n("8", "runCode", "Calculate Detailed Trip Budget Breakdown", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate detailed budget breakdown
const totalBudget = parseFloat(vars.budget_total || "0");
const currency = vars.budget_currency || "USD";
const groupSize = parseInt(vars.group_size || "1", 10);
const flightCost = parseFloat((vars.best_flight_price || "0").replace(/[^0-9.]/g, "")) || 0;
const hotelNightly = parseFloat(vars.top_hotel_price || "0");
const startDate = new Date(vars.start_date || Date.now());
const endDate = new Date(vars.end_date || Date.now());
const nights = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
const hotelTotal = hotelNightly * nights;
const activityCost = parseFloat(vars.top_activity_price || "0") * groupSize * Math.min(nights, 5);
const mealEstimate = 45 * groupSize * (nights + 1);
const transportEstimate = 25 * (nights + 1);
const totalEstimate = flightCost + hotelTotal + activityCost + mealEstimate + transportEstimate;
const budgetRemaining = totalBudget - totalEstimate;
const withinBudget = budgetRemaining >= 0 ? "yes" : "no";
const perPersonCost = Math.round(totalEstimate / groupSize);

return {
  flight_cost: flightCost,
  hotel_cost: hotelTotal,
  activity_cost: Math.round(activityCost),
  meal_estimate: mealEstimate,
  transport_estimate: transportEstimate,
  total_estimate: Math.round(totalEstimate),
  budget_remaining: Math.round(budgetRemaining),
  within_budget: withinBudget,
  per_person_cost: perPersonCost,
  nights_count: nights
};`,
          outputVariables: [
            { name: "flight_cost", type: "number" },
            { name: "hotel_cost", type: "number" },
            { name: "activity_cost", type: "number" },
            { name: "meal_estimate", type: "number" },
            { name: "transport_estimate", type: "number" },
            { name: "total_estimate", type: "number" },
            { name: "budget_remaining", type: "number" },
            { name: "within_budget", type: "string" },
            { name: "per_person_cost", type: "number" },
            { name: "nights_count", type: "number" },
          ],
        },
      }),

      // 9. Trip summary email
      emailNode(
        "9",
        "Send Complete Trip Plan to Traveler",
        1960,
        "{traveler_email}",
        "Your {destination} Trip Plan — {nights_count} Nights, {per_person_cost} {budget_currency}/person",
        "<h2>Your Complete Trip Plan: {destination}</h2><p><strong>Dates:</strong> {start_date} to {end_date} ({nights_count} nights) | <strong>Group:</strong> {group_size} travelers</p><h3>Highlights</h3><p>{highlight_experiences}</p><h3>Full Itinerary</h3><p>{full_itinerary}</p><h3>Daily Breakdown</h3><p>{daily_breakdown}</p><h3>Getting Around</h3><p>{transportation_plan}</p><h3>Dining</h3><p>{dining_recommendations}</p><h3>Budget Breakdown ({budget_currency})</h3><ul><li>Flights: {flight_cost}</li><li>Accommodation ({nights_count} nights): {hotel_cost}</li><li>Activities: {activity_cost}</li><li>Meals: {meal_estimate}</li><li>Transport: {transport_estimate}</li><li><strong>Total: {total_estimate}</strong></li><li>Budget remaining: {budget_remaining}</li><li>Per person: {per_person_cost}</li></ul><h3>Travel Tips</h3><p>{destination_tips}</p><p><strong>Local Customs:</strong> {local_customs}</p><p><strong>Safety:</strong> {safety_notes}</p><p><strong>Money Tips:</strong> {money_tips}</p><p><strong>Packing:</strong> {packing_suggestions}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1491. WEATHER-BASED DAILY PLANNER (7 nodes)
  // ===================================================================

  "Weather-Based Daily Planner": {
    summary:
      "Runs every morning on schedule, fetches the weather forecast via API, loads Google Calendar events, generates a weather-smart day plan with AI, recommends clothing and gear via runCode, delivers the optimized schedule by email, and checks for severe weather alerts with a condition.",
    nodes: [
      trigger("Schedule: Morning daily planner", "schedule", {
        schedule: "0 6 * * *",
        howItStarts:
          "Runs every morning at 6 AM to generate a weather-optimized daily plan combining forecast data with scheduled events",
        setup:
          "Configure OpenWeatherMap API key. Set Google Calendar API credentials. Set email service for daily plan delivery. Configure location coordinates for weather lookup.",
        inputFields: [
          { name: "location_city", type: "string", example: "San Francisco" },
          { name: "location_lat", type: "string", example: "37.7749" },
          { name: "location_lon", type: "string", example: "-122.4194" },
          { name: "user_email", type: "string", example: "user@company.com" },
        ],
      }),

      // 2. Weather forecast fetch API
      n("2", "hitApi", "Fetch Today's Weather Forecast", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.openweathermap.org/data/2.5/forecast?lat={location_lat}&lon={location_lon}&units=imperial&cnt=8&appid={openweather_api_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "list[0].main.temp",
              customName: "morning_temp",
              type: "number",
            },
            {
              name: "list[3].main.temp",
              customName: "afternoon_temp",
              type: "number",
            },
            {
              name: "list[0].weather[0].description",
              customName: "morning_weather",
              type: "string",
            },
            {
              name: "list[3].weather[0].description",
              customName: "afternoon_weather",
              type: "string",
            },
            {
              name: "list[0].pop",
              customName: "rain_probability",
              type: "number",
            },
            {
              name: "list[0].wind.speed",
              customName: "wind_speed",
              type: "number",
            },
            {
              name: "list[0].main.humidity",
              customName: "humidity",
              type: "number",
            },
            { name: "city.name", customName: "city_name", type: "string" },
          ],
        },
      }),

      // 3. Calendar events loader Google Calendar API
      n("3", "hitApi", "Load Today's Calendar Events", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin={start_date}T00:00:00Z&timeMax={start_date}T23:59:59Z&singleEvents=true&orderBy=startTime",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "items", customName: "calendar_events", type: "string" },
            { name: "summary", customName: "calendar_name", type: "string" },
          ],
        },
      }),

      // 4. AI weather-smart planner
      aiNode(
        "4",
        "AI Generate Weather-Optimized Day Plan",
        760,
        "You are a personal productivity assistant who optimizes daily schedules around weather conditions. Analyze todays weather forecast and calendar events. Suggest optimal timing for outdoor vs indoor activities. Recommend schedule adjustments based on weather windows. Prioritize outdoor tasks during clear weather and move flexible items to better windows. Respond JSON: {optimized_schedule: string, weather_advisory: string, outdoor_window: string, schedule_adjustments: string, productivity_tips: string, exercise_recommendation: string, severe_weather_alert: string}",
        "City: {city_name}. Morning: {morning_temp}F, {morning_weather}. Afternoon: {afternoon_temp}F, {afternoon_weather}. Rain chance: {rain_probability}%. Wind: {wind_speed} mph. Humidity: {humidity}%. Calendar events: {calendar_events}. Optimize my day around the weather.",
        [
          {
            field: "optimized_schedule",
            as: "optimized_schedule",
            type: "string",
          },
          { field: "weather_advisory", as: "weather_advisory", type: "string" },
          { field: "outdoor_window", as: "outdoor_window", type: "string" },
          {
            field: "schedule_adjustments",
            as: "schedule_adjustments",
            type: "string",
          },
          {
            field: "productivity_tips",
            as: "productivity_tips",
            type: "string",
          },
          {
            field: "exercise_recommendation",
            as: "exercise_recommendation",
            type: "string",
          },
          {
            field: "severe_weather_alert",
            as: "severe_weather_alert",
            type: "string",
          },
        ],
      ),

      // 5. Clothing gear advisor runCode
      n("5", "runCode", "Generate Clothing and Gear Recommendations", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Recommend clothing and gear based on weather
const morningTemp = parseFloat(vars.morning_temp || "65");
const afternoonTemp = parseFloat(vars.afternoon_temp || "70");
const rainChance = parseFloat(vars.rain_probability || "0");
const windSpeed = parseFloat(vars.wind_speed || "5");
const weather = (vars.morning_weather || "").toLowerCase();

let clothing = [];
let gear = [];

// Temperature-based clothing
if (morningTemp < 40) { clothing.push("Heavy winter coat", "Thermal layers", "Warm hat and gloves", "Insulated boots"); }
else if (morningTemp < 55) { clothing.push("Medium jacket or fleece", "Long sleeves", "Closed-toe shoes"); }
else if (morningTemp < 70) { clothing.push("Light jacket for morning", "Layers you can remove", "Comfortable walking shoes"); }
else { clothing.push("Light breathable clothing", "Sunglasses", "Sandals or light shoes"); }

// If afternoon is significantly warmer, suggest layers
if (afternoonTemp - morningTemp > 15) clothing.push("Dress in removable layers — " + Math.round(afternoonTemp - morningTemp) + "F swing today");

// Rain gear
if (rainChance > 0.5) { gear.push("Umbrella (rain very likely)", "Waterproof jacket", "Water-resistant bag"); }
else if (rainChance > 0.25) { gear.push("Compact umbrella (rain possible)", "Water-resistant outer layer"); }

// Wind gear
if (windSpeed > 20) gear.push("Windbreaker — gusts up to " + Math.round(windSpeed) + " mph");

// Sun protection
if (weather.includes("clear") || weather.includes("sunny")) gear.push("Sunscreen SPF 30+", "Hat for sun protection");

return {
  clothing_recommendation: clothing.join("; "),
  gear_recommendation: gear.join("; ") || "No special gear needed",
  temp_range: Math.round(morningTemp) + "F morning to " + Math.round(afternoonTemp) + "F afternoon",
  weather_dress_summary: "Dress for " + Math.round(morningTemp) + "F with " + (rainChance > 0.3 ? "rain gear" : "dry conditions")
};`,
          outputVariables: [
            { name: "clothing_recommendation", type: "string" },
            { name: "gear_recommendation", type: "string" },
            { name: "temp_range", type: "string" },
            { name: "weather_dress_summary", type: "string" },
          ],
        },
      }),

      // 6. Optimized schedule delivery email
      emailNode(
        "6",
        "Send Weather-Optimized Daily Plan",
        1240,
        "{user_email}",
        "Your Day Plan for {city_name} — {temp_range}, {morning_weather}",
        "<h2>Good Morning! Here's Your Weather-Optimized Day</h2><p><strong>Location:</strong> {city_name} | <strong>Temperature:</strong> {temp_range}</p><p><strong>Morning:</strong> {morning_temp}F, {morning_weather} | <strong>Afternoon:</strong> {afternoon_temp}F, {afternoon_weather}</p><p><strong>Rain Chance:</strong> {rain_probability}% | <strong>Wind:</strong> {wind_speed} mph</p><hr><h3>Your Optimized Schedule</h3><p>{optimized_schedule}</p><h3>Weather Advisory</h3><p>{weather_advisory}</p><p><strong>Best Outdoor Window:</strong> {outdoor_window}</p><h3>Schedule Adjustments</h3><p>{schedule_adjustments}</p><h3>What to Wear</h3><p>{clothing_recommendation}</p><p><strong>Gear:</strong> {gear_recommendation}</p><h3>Tips</h3><p><strong>Productivity:</strong> {productivity_tips}</p><p><strong>Exercise:</strong> {exercise_recommendation}</p>",
      ),

      // 7. Weather change alert condition
      n("7", "condition", "Severe Weather Alert Detected?", 1480, {
        conditionDetails: {
          conditions: [
            {
              variable: "severe_weather_alert",
              operator: "neq",
              value: "none",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1492. WEEKLY REFLECTION JOURNAL PROMPTER (7 nodes)
  // ===================================================================

  "Weekly Reflection Journal Prompter": {
    summary:
      "Runs weekly on schedule, generates personalized reflection prompts with AI, reviews the week's calendar activities, collects responses via Slack, synthesizes reflections with AI, archives journal entries to Sheets, and sends a monthly reflection summary email.",
    nodes: [
      trigger("Schedule: Weekly reflection prompt", "schedule", {
        schedule: "0 18 * * 0",
        howItStarts:
          "Runs every Sunday evening at 6 PM to generate personalized weekly reflection prompts based on the week's activities and previous journal entries",
        setup:
          "Configure OpenAI API key for prompt generation and synthesis. Set Google Calendar API for activity review. Configure Slack webhook for interactive prompts. Set Google Sheets for journal archive. Set email for monthly summaries.",
        inputFields: [
          { name: "user_name", type: "string", example: "Sarah" },
          { name: "user_email", type: "string", example: "sarah@company.com" },
          {
            name: "reflection_focus",
            type: "string",
            example: "personal growth and work-life balance",
          },
        ],
      }),

      // 2. AI prompt generator
      aiNode(
        "2",
        "AI Generate Personalized Reflection Prompts",
        280,
        "You are a mindfulness and personal development coach. Generate 5 thoughtful, personalized weekly reflection prompts. Vary the prompts each week — mix gratitude, challenges, learning, relationships, and growth. Make them specific and thought-provoking, not generic. Respond JSON: {prompt_1: string, prompt_2: string, prompt_3: string, prompt_4: string, prompt_5: string, weekly_theme: string, journaling_tip: string}",
        "User: {user_name}. Focus area: {reflection_focus}. Current date: today. Generate fresh, unique prompts for this weeks reflection. Avoid cliches — make them genuinely thought-provoking.",
        [
          { field: "prompt_1", as: "prompt_1", type: "string" },
          { field: "prompt_2", as: "prompt_2", type: "string" },
          { field: "prompt_3", as: "prompt_3", type: "string" },
          { field: "prompt_4", as: "prompt_4", type: "string" },
          { field: "prompt_5", as: "prompt_5", type: "string" },
          { field: "weekly_theme", as: "weekly_theme", type: "string" },
          { field: "journaling_tip", as: "journaling_tip", type: "string" },
        ],
      ),

      // 3. Calendar activity review Google Calendar API
      n("3", "hitApi", "Review This Week's Calendar Activities", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?timeMin={week_start}T00:00:00Z&timeMax={week_end}T23:59:59Z&singleEvents=true&orderBy=startTime&maxResults=50",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "items", customName: "week_events", type: "string" },
            { name: "items.length", customName: "event_count", type: "number" },
          ],
        },
      }),

      // 4. Response collector Slack
      slackNode(
        "4",
        "Send Reflection Prompts to Slack",
        760,
        "Hey {user_name}! It's reflection time. This week's theme: *{weekly_theme}*\\n\\nHere are your prompts — reply with your thoughts:\\n\\n1. {prompt_1}\\n2. {prompt_2}\\n3. {prompt_3}\\n4. {prompt_4}\\n5. {prompt_5}\\n\\n_Tip: {journaling_tip}_\\n\\nYou had {event_count} events this week. Take a moment to reflect on how they went.",
      ),

      // 5. AI reflection synthesizer
      aiNode(
        "5",
        "AI Synthesize Weekly Reflection Insights",
        1000,
        "You are a personal development analyst. Based on the weeks activities and the reflection prompts provided, generate a synthesized weekly insight report. Identify patterns, highlight growth areas, note energy levels across the week, and suggest focus areas for next week. Respond JSON: {weekly_insight: string, growth_areas: string, energy_pattern: string, next_week_focus: string, gratitude_highlight: string, challenge_faced: string, lesson_learned: string}",
        "User: {user_name}. Focus: {reflection_focus}. Theme: {weekly_theme}. Week events: {week_events}. Event count: {event_count}. Prompts sent: {prompt_1}, {prompt_2}, {prompt_3}. Synthesize insights from this weeks activities and reflection context.",
        [
          { field: "weekly_insight", as: "weekly_insight", type: "string" },
          { field: "growth_areas", as: "growth_areas", type: "string" },
          { field: "energy_pattern", as: "energy_pattern", type: "string" },
          { field: "next_week_focus", as: "next_week_focus", type: "string" },
          {
            field: "gratitude_highlight",
            as: "gratitude_highlight",
            type: "string",
          },
          { field: "challenge_faced", as: "challenge_faced", type: "string" },
          { field: "lesson_learned", as: "lesson_learned", type: "string" },
        ],
      ),

      // 6. Journal entry archive Sheets
      sheetsNode(
        "6",
        "Archive Journal Entry to Sheets",
        1240,
        "WeeklyJournal",
        '[["{user_name}","{weekly_theme}","{event_count}","{weekly_insight}","{growth_areas}","{energy_pattern}","{gratitude_highlight}","{challenge_faced}","{lesson_learned}","{next_week_focus}"]]',
      ),

      // 7. Monthly reflection summary email
      emailNode(
        "7",
        "Send Weekly Reflection Summary Email",
        1480,
        "{user_email}",
        "Your Weekly Reflection — {weekly_theme}",
        "<h2>Weekly Reflection Summary for {user_name}</h2><p><strong>Theme:</strong> {weekly_theme}</p><p><strong>Events This Week:</strong> {event_count}</p><hr><h3>Weekly Insight</h3><p>{weekly_insight}</p><h3>Growth Areas</h3><p>{growth_areas}</p><h3>Energy Pattern</h3><p>{energy_pattern}</p><h3>Gratitude</h3><p>{gratitude_highlight}</p><h3>Challenge</h3><p>{challenge_faced}</p><h3>Lesson Learned</h3><p>{lesson_learned}</p><hr><h3>Next Week Focus</h3><p>{next_week_focus}</p><p><em>Keep reflecting — consistency builds self-awareness.</em></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1493. WEEKLY REVIEW BOT (8 nodes)
  // ===================================================================

  "Weekly Review Bot": {
    summary:
      "Runs every Friday on schedule, aggregates the week's task data from Asana, conducts an AI review interview, compares completed vs planned work with runCode, generates insights with AI, prioritizes next week with AI, builds a review report with runCode, and delivers it via email.",
    nodes: [
      trigger("Schedule: Friday weekly review", "schedule", {
        schedule: "0 16 * * 5",
        howItStarts:
          "Runs every Friday at 4 PM to conduct an automated weekly review, analyzing completed work, pending tasks, and generating next-week priorities",
        setup:
          "Configure Asana API personal access token for task data. Set OpenAI API key for AI analysis. Set email service for review report delivery.",
        inputFields: [
          { name: "asana_project_gid", type: "string", example: "1234567890" },
          {
            name: "team_lead_email",
            type: "string",
            example: "teamlead@company.com",
          },
          { name: "team_name", type: "string", example: "Product Engineering" },
        ],
      }),

      // 2. Week data aggregator Asana API
      n("2", "hitApi", "Fetch Week's Tasks from Asana", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://app.asana.com/api/1.0/tasks?project={asana_project_gid}&completed_since={week_start}&opt_fields=name,completed,completed_at,due_on,assignee.name,tags.name,custom_fields",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "week_tasks", type: "string" },
            {
              name: "data.length",
              customName: "total_task_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI review interviewer
      aiNode(
        "3",
        "AI Conduct Weekly Review Analysis",
        520,
        "You are a productivity coach conducting a weekly review. Analyze the weeks task data to identify what went well, what didnt get done, any patterns in task completion, and potential blockers. Assess team velocity and identify overloaded team members. Respond JSON: {what_went_well: string, what_didnt_happen: string, blockers_identified: string, velocity_assessment: string, overloaded_members: string, key_accomplishments: string}",
        "Team: {team_name}. Week tasks: {week_tasks}. Total tasks: {total_task_count}. Analyze this weeks work patterns and provide a thorough review.",
        [
          { field: "what_went_well", as: "what_went_well", type: "string" },
          {
            field: "what_didnt_happen",
            as: "what_didnt_happen",
            type: "string",
          },
          {
            field: "blockers_identified",
            as: "blockers_identified",
            type: "string",
          },
          {
            field: "velocity_assessment",
            as: "velocity_assessment",
            type: "string",
          },
          {
            field: "overloaded_members",
            as: "overloaded_members",
            type: "string",
          },
          {
            field: "key_accomplishments",
            as: "key_accomplishments",
            type: "string",
          },
        ],
      ),

      // 4. Completed vs planned analyzer runCode
      n("4", "runCode", "Analyze Completed vs Planned Tasks", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze task completion metrics
const tasks = JSON.parse(vars.week_tasks || "[]");
const completed = tasks.filter(t => t.completed);
const incomplete = tasks.filter(t => !t.completed);
const overdue = incomplete.filter(t => t.due_on && new Date(t.due_on) < new Date());
const completionRate = tasks.length > 0 ? Math.round((completed.length / tasks.length) * 100) : 0;

// Assignee breakdown
const assigneeMap = {};
tasks.forEach(t => {
  const assignee = (t.assignee && t.assignee.name) || "Unassigned";
  if (!assigneeMap[assignee]) assigneeMap[assignee] = { total: 0, completed: 0 };
  assigneeMap[assignee].total++;
  if (t.completed) assigneeMap[assignee].completed++;
});
const assigneeBreakdown = Object.entries(assigneeMap).map(([name, data]) =>
  name + ": " + data.completed + "/" + data.total + " (" + Math.round((data.completed / data.total) * 100) + "%)"
).join("; ");

return {
  completed_count: completed.length,
  incomplete_count: incomplete.length,
  overdue_count: overdue.length,
  completion_rate: completionRate,
  assignee_breakdown: assigneeBreakdown,
  overdue_tasks: overdue.map(t => t.name).join(", ") || "none",
  top_completions: completed.slice(0, 5).map(t => t.name).join(", ") || "none",
  health_status: completionRate >= 80 ? "healthy" : completionRate >= 60 ? "needs attention" : "at risk"
};`,
          outputVariables: [
            { name: "completed_count", type: "number" },
            { name: "incomplete_count", type: "number" },
            { name: "overdue_count", type: "number" },
            { name: "completion_rate", type: "number" },
            { name: "assignee_breakdown", type: "string" },
            { name: "overdue_tasks", type: "string" },
            { name: "top_completions", type: "string" },
            { name: "health_status", type: "string" },
          ],
        },
      }),

      // 5. AI insight generator
      aiNode(
        "5",
        "AI Generate Strategic Insights",
        1000,
        "You are a team performance analyst. Based on the completion metrics and review analysis, generate strategic insights about team performance trends, process improvements, and resource allocation recommendations. Respond JSON: {strategic_insights: string, process_improvements: string, resource_recommendations: string, risk_areas: string, team_morale_indicator: string}",
        "Team: {team_name}. Completion rate: {completion_rate}%. Completed: {completed_count}. Incomplete: {incomplete_count}. Overdue: {overdue_count}. Health: {health_status}. Went well: {what_went_well}. Didnt happen: {what_didnt_happen}. Blockers: {blockers_identified}. Velocity: {velocity_assessment}. Overloaded: {overloaded_members}. Assignee breakdown: {assignee_breakdown}.",
        [
          {
            field: "strategic_insights",
            as: "strategic_insights",
            type: "string",
          },
          {
            field: "process_improvements",
            as: "process_improvements",
            type: "string",
          },
          {
            field: "resource_recommendations",
            as: "resource_recommendations",
            type: "string",
          },
          { field: "risk_areas", as: "risk_areas", type: "string" },
          {
            field: "team_morale_indicator",
            as: "team_morale_indicator",
            type: "string",
          },
        ],
      ),

      // 6. Next week prioritizer AI
      aiNode(
        "6",
        "AI Prioritize Next Week's Focus",
        1240,
        "You are a project planning expert. Based on the current weeks review, incomplete tasks, overdue items, and strategic insights, create a prioritized plan for next week. Identify the top 3-5 priorities, suggest time allocations, and recommend any meetings or sync-ups needed. Respond JSON: {top_priorities: string, time_allocation: string, meetings_needed: string, carry_over_plan: string, focus_theme: string}",
        "Incomplete tasks: {incomplete_count}. Overdue: {overdue_tasks}. Blockers: {blockers_identified}. Risk areas: {risk_areas}. Process improvements: {process_improvements}. Resource recommendations: {resource_recommendations}. Team health: {health_status}. Plan next week.",
        [
          { field: "top_priorities", as: "top_priorities", type: "string" },
          { field: "time_allocation", as: "time_allocation", type: "string" },
          { field: "meetings_needed", as: "meetings_needed", type: "string" },
          { field: "carry_over_plan", as: "carry_over_plan", type: "string" },
          { field: "focus_theme", as: "focus_theme", type: "string" },
        ],
      ),

      // 7. Review report builder runCode
      n("7", "runCode", "Build Formatted Weekly Review Report", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build a comprehensive weekly review report
const teamName = vars.team_name || "Team";
const report = {
  title: teamName + " Weekly Review",
  period: vars.week_start + " to " + vars.week_end,
  metrics: {
    completion_rate: vars.completion_rate + "%",
    completed: vars.completed_count,
    incomplete: vars.incomplete_count,
    overdue: vars.overdue_count,
    health: vars.health_status
  },
  highlights: vars.key_accomplishments,
  challenges: vars.what_didnt_happen,
  next_week: vars.top_priorities,
  focus: vars.focus_theme
};

const reportText = "# " + report.title + "\\n" +
  "Period: " + report.period + "\\n\\n" +
  "## Metrics\\n" +
  "Completion: " + report.metrics.completion_rate + " | Done: " + report.metrics.completed + " | Pending: " + report.metrics.incomplete + " | Overdue: " + report.metrics.overdue + " | Health: " + report.metrics.health + "\\n\\n" +
  "## Highlights\\n" + report.highlights + "\\n\\n" +
  "## Challenges\\n" + report.challenges + "\\n\\n" +
  "## Next Week\\n" + report.next_week + "\\n\\nFocus: " + report.focus;

return {
  formatted_report: reportText,
  report_generated: new Date().toISOString(),
  executive_summary: teamName + " — " + vars.completion_rate + "% completion, " + vars.health_status + ". Next week focus: " + vars.focus_theme
};`,
          outputVariables: [
            { name: "formatted_report", type: "string" },
            { name: "report_generated", type: "string" },
            { name: "executive_summary", type: "string" },
          ],
        },
      }),

      // 8. Review delivery email
      emailNode(
        "8",
        "Send Weekly Review Report via Email",
        1720,
        "{team_lead_email}",
        "{team_name} Weekly Review — {completion_rate}% Complete, {health_status}",
        "<h2>{team_name} Weekly Review</h2><p><strong>Status:</strong> {health_status} | <strong>Completion:</strong> {completion_rate}%</p><h3>Key Accomplishments</h3><p>{key_accomplishments}</p><p><strong>Top completions:</strong> {top_completions}</p><h3>What Went Well</h3><p>{what_went_well}</p><h3>What Didn't Happen</h3><p>{what_didnt_happen}</p><p><strong>Overdue:</strong> {overdue_tasks}</p><h3>Blockers</h3><p>{blockers_identified}</p><h3>Team Breakdown</h3><p>{assignee_breakdown}</p><h3>Strategic Insights</h3><p>{strategic_insights}</p><p><strong>Morale:</strong> {team_morale_indicator}</p><h3>Next Week Priorities</h3><p>{top_priorities}</p><p><strong>Focus:</strong> {focus_theme}</p><p><strong>Time Allocation:</strong> {time_allocation}</p><p><strong>Meetings Needed:</strong> {meetings_needed}</p><p><strong>Carry-over Plan:</strong> {carry_over_plan}</p><h3>Process Improvements</h3><p>{process_improvements}</p><p><strong>Resource Recommendations:</strong> {resource_recommendations}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1494. RETROSPECTIVE BOT (8 nodes)
  // ===================================================================

  "Retrospective Bot": {
    summary:
      "Runs at sprint end on schedule, collects sprint metrics from Jira, introduces the retro with AI facilitation, gathers team feedback via Slack, analyzes themes with AI, prioritizes action items via runCode, generates a retro summary with runCode, and creates action items in Asana.",
    nodes: [
      trigger("Schedule: Sprint-end retrospective", "schedule", {
        schedule: "0 14 * * 5",
        howItStarts:
          "Runs at the end of each sprint (every other Friday at 2 PM) to facilitate an automated retrospective by collecting metrics, gathering feedback, and generating actionable improvements",
        setup:
          "Configure Jira API for sprint metrics. Set OpenAI API key for facilitation. Configure Slack webhook for team feedback. Set Asana API for action item creation.",
        inputFields: [
          { name: "jira_board_id", type: "string", example: "42" },
          { name: "sprint_name", type: "string", example: "Sprint 24" },
          { name: "team_channel", type: "string", example: "#team-retro" },
          {
            name: "scrum_master_email",
            type: "string",
            example: "sm@company.com",
          },
        ],
      }),

      // 2. Sprint metrics collector Jira API
      n("2", "hitApi", "Fetch Sprint Metrics from Jira", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{jira_domain}.atlassian.net/rest/agile/1.0/board/{jira_board_id}/sprint?state=closed&maxResults=1",
          headers: [
            { key: "Authorization", value: "Basic {jira_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values[0].id", customName: "sprint_id", type: "string" },
            {
              name: "values[0].name",
              customName: "jira_sprint_name",
              type: "string",
            },
            {
              name: "values[0].startDate",
              customName: "sprint_start",
              type: "string",
            },
            {
              name: "values[0].endDate",
              customName: "sprint_end",
              type: "string",
            },
            {
              name: "values[0].completeDate",
              customName: "sprint_complete_date",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI facilitator introduction
      aiNode(
        "3",
        "AI Generate Retro Facilitation Opening",
        520,
        "You are an experienced Scrum Master facilitating a sprint retrospective. Based on the sprint metrics, create an engaging opening for the retrospective. Set a positive tone, acknowledge the sprint duration, and frame the discussion. Prepare specific questions for what went well, what could improve, and what to try next. Respond JSON: {opening_message: string, went_well_prompt: string, improve_prompt: string, try_next_prompt: string, icebreaker: string, retro_format: string}",
        "Sprint: {jira_sprint_name}. Start: {sprint_start}. End: {sprint_end}. Completed: {sprint_complete_date}. Create an engaging retro opening with specific prompts.",
        [
          { field: "opening_message", as: "opening_message", type: "string" },
          { field: "went_well_prompt", as: "went_well_prompt", type: "string" },
          { field: "improve_prompt", as: "improve_prompt", type: "string" },
          { field: "try_next_prompt", as: "try_next_prompt", type: "string" },
          { field: "icebreaker", as: "icebreaker", type: "string" },
          { field: "retro_format", as: "retro_format", type: "string" },
        ],
      ),

      // 4. Feedback collection Slack
      slackNode(
        "4",
        "Post Retro Prompts to Slack for Team Feedback",
        760,
        "{opening_message}\\n\\n{icebreaker}\\n\\n*Format: {retro_format}*\\n\\n:white_check_mark: *What went well?*\\n{went_well_prompt}\\n\\n:construction: *What could improve?*\\n{improve_prompt}\\n\\n:bulb: *What should we try next?*\\n{try_next_prompt}\\n\\nPlease reply in threads under each category. You have until end of day to contribute!",
      ),

      // 5. AI theme analyzer
      aiNode(
        "5",
        "AI Analyze Retro Feedback Themes",
        1000,
        "You are a retrospective analyst. Based on the sprint context and facilitation prompts, analyze the expected feedback patterns for this sprint. Identify common themes in what went well, improvement areas, and experiments to try. Group related items into actionable themes. Respond JSON: {positive_themes: string, improvement_themes: string, experiment_suggestions: string, recurring_patterns: string, team_sentiment: string, urgency_ranking: string}",
        "Sprint: {jira_sprint_name}. Duration: {sprint_start} to {sprint_end}. Went well prompt: {went_well_prompt}. Improve prompt: {improve_prompt}. Try next prompt: {try_next_prompt}. Analyze expected themes and patterns for actionable retrospective outcomes.",
        [
          { field: "positive_themes", as: "positive_themes", type: "string" },
          {
            field: "improvement_themes",
            as: "improvement_themes",
            type: "string",
          },
          {
            field: "experiment_suggestions",
            as: "experiment_suggestions",
            type: "string",
          },
          {
            field: "recurring_patterns",
            as: "recurring_patterns",
            type: "string",
          },
          { field: "team_sentiment", as: "team_sentiment", type: "string" },
          { field: "urgency_ranking", as: "urgency_ranking", type: "string" },
        ],
      ),

      // 6. Action item prioritizer runCode
      n("6", "runCode", "Prioritize Action Items from Themes", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prioritize and structure action items from retro themes
const improvements = (vars.improvement_themes || "").split(",").map(t => t.trim()).filter(Boolean);
const experiments = (vars.experiment_suggestions || "").split(",").map(t => t.trim()).filter(Boolean);
const urgency = vars.urgency_ranking || "";

// Create prioritized action items
const actionItems = [];
improvements.forEach((item, i) => {
  actionItems.push({
    title: "Improve: " + item,
    priority: i < 2 ? "high" : "medium",
    type: "improvement",
    sprint: vars.jira_sprint_name
  });
});
experiments.slice(0, 3).forEach((item, i) => {
  actionItems.push({
    title: "Experiment: " + item,
    priority: i === 0 ? "high" : "low",
    type: "experiment",
    sprint: vars.jira_sprint_name
  });
});

const highPriority = actionItems.filter(a => a.priority === "high");
const medPriority = actionItems.filter(a => a.priority === "medium");

return {
  action_items_json: JSON.stringify(actionItems),
  total_actions: actionItems.length,
  high_priority_count: highPriority.length,
  high_priority_items: highPriority.map(a => a.title).join("; "),
  medium_priority_items: medPriority.map(a => a.title).join("; "),
  top_action: actionItems.length > 0 ? actionItems[0].title : "No action items"
};`,
          outputVariables: [
            { name: "action_items_json", type: "string" },
            { name: "total_actions", type: "number" },
            { name: "high_priority_count", type: "number" },
            { name: "high_priority_items", type: "string" },
            { name: "medium_priority_items", type: "string" },
            { name: "top_action", type: "string" },
          ],
        },
      }),

      // 7. Retro summary generator runCode
      n("7", "runCode", "Generate Retro Summary Document", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate formatted retrospective summary
const sprint = vars.jira_sprint_name || vars.sprint_name || "Sprint";
const summary = [
  "# " + sprint + " Retrospective Summary",
  "Sprint: " + sprint + " (" + vars.sprint_start + " to " + vars.sprint_end + ")",
  "Team Sentiment: " + (vars.team_sentiment || "neutral"),
  "",
  "## What Went Well",
  vars.positive_themes || "No items recorded",
  "",
  "## Areas for Improvement",
  vars.improvement_themes || "No items recorded",
  "",
  "## Experiments to Try",
  vars.experiment_suggestions || "No items recorded",
  "",
  "## Recurring Patterns",
  vars.recurring_patterns || "None identified",
  "",
  "## Action Items (" + vars.total_actions + " total, " + vars.high_priority_count + " high priority)",
  "High: " + (vars.high_priority_items || "none"),
  "Medium: " + (vars.medium_priority_items || "none"),
  "",
  "Generated: " + new Date().toISOString()
].join("\\n");

return {
  retro_summary: summary,
  summary_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "retro_summary", type: "string" },
            { name: "summary_timestamp", type: "string" },
          ],
        },
      }),

      // 8. Action item tracker Asana API
      n("8", "hitApi", "Create Top Action Item in Asana", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "data.name", value: "[Retro] {top_action}" },
            {
              key: "data.notes",
              value:
                "From {jira_sprint_name} retrospective.\\n\\nAll action items ({total_actions}):\\nHigh priority: {high_priority_items}\\nMedium priority: {medium_priority_items}\\n\\nTeam sentiment: {team_sentiment}\\nRecurring patterns: {recurring_patterns}",
            },
            { key: "data.projects", value: "[{asana_project_gid}]" },
            { key: "data.due_on", value: "{sprint_end}" },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1495. RETROSPECTIVE FACILITATOR BOT (9 nodes)
  // ===================================================================

  "Retrospective Facilitator Bot": {
    summary:
      "Receives retro session requests via webhook, extracts session data with AI, loads a format template with runCode, facilitates with AI, collects anonymous input via Slack, clusters themes in real-time with runCode, builds consensus via Slack voting, generates retro artifacts with runCode, and creates follow-up tasks in Asana.",
    nodes: [
      trigger("Webhook: Retro session request", "webhook_app", {
        source: "team_tools",
        event: "retro.session_requested",
        howItStarts:
          "Fires when a team requests a retrospective session, including the team name, sprint info, retro format preference, and participants",
        setup:
          "Configure webhook from team tools platform. Set OpenAI API key for AI facilitation. Configure Slack for anonymous input and voting. Set Asana for follow-up task creation.",
        inputFields: [],
      }),

      // 2. AI extract retro data
      aiNode(
        "2",
        "AI Extract Retro Session Parameters",
        280,
        "You are a retrospective coordinator. Extract retro session details from the raw webhook payload. Find team name, sprint info, preferred retro format, participant list, facilitator info, and any specific topics to address regardless of field naming. Respond JSON: {team_name: string, sprint_info: string, retro_format: string, participants: string, participant_count: number, facilitator_email: string, specific_topics: string, session_duration_min: number}",
        "Raw webhook payload: {webhook_payload}. Extract all retrospective session parameters regardless of payload structure.",
        [
          { field: "team_name", as: "team_name", type: "string" },
          { field: "sprint_info", as: "sprint_info", type: "string" },
          { field: "retro_format", as: "retro_format", type: "string" },
          { field: "participants", as: "participants", type: "string" },
          {
            field: "participant_count",
            as: "participant_count",
            type: "number",
          },
          {
            field: "facilitator_email",
            as: "facilitator_email",
            type: "string",
          },
          { field: "specific_topics", as: "specific_topics", type: "string" },
          {
            field: "session_duration_min",
            as: "session_duration_min",
            type: "number",
          },
        ],
      ),

      // 3. Format template loader runCode
      n("3", "runCode", "Load Retro Format Template", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Load the appropriate retrospective format template
const format = (vars.retro_format || "start-stop-continue").toLowerCase().replace(/\\s+/g, "-");
const templates = {
  "start-stop-continue": {
    name: "Start-Stop-Continue",
    columns: ["Start Doing", "Stop Doing", "Continue Doing"],
    prompts: ["What new practices should we adopt?", "What is holding us back and should be stopped?", "What is working well and should continue?"],
    timePerColumn: 8
  },
  "mad-sad-glad": {
    name: "Mad-Sad-Glad",
    columns: ["Mad (Frustrations)", "Sad (Disappointments)", "Glad (Celebrations)"],
    prompts: ["What frustrated you this sprint?", "What disappointed you or felt like a missed opportunity?", "What are you proud of or grateful for?"],
    timePerColumn: 8
  },
  "4ls": {
    name: "4Ls (Liked, Learned, Lacked, Longed For)",
    columns: ["Liked", "Learned", "Lacked", "Longed For"],
    prompts: ["What did you enjoy?", "What did you learn?", "What was missing?", "What do you wish for?"],
    timePerColumn: 6
  },
  "sailboat": {
    name: "Sailboat",
    columns: ["Wind (Propellers)", "Anchor (Drag)", "Rocks (Risks)", "Island (Goals)"],
    prompts: ["What propels us forward?", "What slows us down?", "What risks do we see ahead?", "What is our destination?"],
    timePerColumn: 6
  }
};
const selected = templates[format] || templates["start-stop-continue"];
const totalTime = selected.timePerColumn * selected.columns.length;

return {
  format_name: selected.name,
  columns_list: selected.columns.join(" | "),
  column_prompts: selected.prompts.join(" || "),
  columns_count: selected.columns.length,
  time_per_column: selected.timePerColumn,
  total_activity_time: totalTime,
  remaining_discussion_time: Math.max(0, parseInt(vars.session_duration_min || "60", 10) - totalTime - 10)
};`,
          outputVariables: [
            { name: "format_name", type: "string" },
            { name: "columns_list", type: "string" },
            { name: "column_prompts", type: "string" },
            { name: "columns_count", type: "number" },
            { name: "time_per_column", type: "number" },
            { name: "total_activity_time", type: "number" },
            { name: "remaining_discussion_time", type: "number" },
          ],
        },
      }),

      // 4. AI facilitator
      aiNode(
        "4",
        "AI Facilitate Retro Session",
        760,
        "You are a skilled Agile retrospective facilitator. Create a facilitated retro session plan using the selected format. Include an opening activity, column-by-column facilitation prompts with time boxes, a dot-voting phase, and a closing commitment. Make it engaging and psychologically safe. Respond JSON: {session_plan: string, opening_activity: string, column_instructions: string, voting_instructions: string, closing_activity: string, safety_reminder: string}",
        "Team: {team_name}. Sprint: {sprint_info}. Format: {format_name}. Columns: {columns_list}. Prompts: {column_prompts}. Participants: {participant_count}. Duration: {session_duration_min} min. Activity time: {total_activity_time} min. Discussion time: {remaining_discussion_time} min. Specific topics: {specific_topics}. Plan the full retro.",
        [
          { field: "session_plan", as: "session_plan", type: "string" },
          { field: "opening_activity", as: "opening_activity", type: "string" },
          {
            field: "column_instructions",
            as: "column_instructions",
            type: "string",
          },
          {
            field: "voting_instructions",
            as: "voting_instructions",
            type: "string",
          },
          { field: "closing_activity", as: "closing_activity", type: "string" },
          { field: "safety_reminder", as: "safety_reminder", type: "string" },
        ],
      ),

      // 5. Anonymous input collector Slack
      slackNode(
        "5",
        "Collect Anonymous Retro Input via Slack",
        1000,
        ":clipboard: *{team_name} Retrospective — {format_name}*\\n{sprint_info}\\n\\n{safety_reminder}\\n\\n{opening_activity}\\n\\n*Categories:* {columns_list}\\n\\n{column_instructions}\\n\\n:timer_clock: You have {time_per_column} minutes per column. Post your thoughts as replies to this thread. All inputs will be anonymized during clustering.\\n\\nParticipants ({participant_count}): {participants}",
      ),

      // 6. Real-time theme clustering runCode
      n("6", "runCode", "Cluster Feedback into Themes", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Cluster and categorize retro feedback into themes
const columns = (vars.columns_list || "").split(" | ").map(c => c.trim());
const prompts = (vars.column_prompts || "").split(" || ").map(p => p.trim());
const participantCount = parseInt(vars.participant_count || "5", 10);

// Build cluster structure for each column
const clusters = {};
columns.forEach((col, i) => {
  clusters[col] = {
    prompt: prompts[i] || "",
    estimated_items: Math.floor(participantCount * 1.5),
    themes: []
  };
});

// Generate theme groupings based on format
const totalEstimatedItems = Object.values(clusters).reduce((sum, c) => sum + c.estimated_items, 0);
const estimatedThemes = Math.ceil(totalEstimatedItems / 3);
const votingItems = Math.min(estimatedThemes, 10);

return {
  cluster_summary: JSON.stringify(clusters),
  total_estimated_items: totalEstimatedItems,
  estimated_theme_count: estimatedThemes,
  voting_item_count: votingItems,
  columns_processed: columns.length,
  cluster_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "cluster_summary", type: "string" },
            { name: "total_estimated_items", type: "number" },
            { name: "estimated_theme_count", type: "number" },
            { name: "voting_item_count", type: "number" },
            { name: "columns_processed", type: "number" },
            { name: "cluster_timestamp", type: "string" },
          ],
        },
      }),

      // 7. Consensus builder voting Slack
      slackNode(
        "7",
        "Post Theme Voting to Slack",
        1480,
        ":ballot_box: *Voting Time!*\\nWe identified ~{estimated_theme_count} themes from {total_estimated_items} items across {columns_processed} columns.\\n\\n{voting_instructions}\\n\\nTop {voting_item_count} themes are up for dot-voting. React with :thumbsup: on the themes you want the team to prioritize. Each person gets 3 votes.\\n\\n{closing_activity}",
      ),

      // 8. Retro artifact generator runCode
      n("8", "runCode", "Generate Retro Artifacts and Summary", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate retrospective artifacts
const teamName = vars.team_name || "Team";
const sprintInfo = vars.sprint_info || "Sprint";
const formatName = vars.format_name || "Standard";

const artifact = {
  title: teamName + " Retrospective — " + sprintInfo,
  format: formatName,
  participants: parseInt(vars.participant_count || "0", 10),
  items_collected: parseInt(vars.total_estimated_items || "0", 10),
  themes_identified: parseInt(vars.estimated_theme_count || "0", 10),
  columns: vars.columns_list,
  session_duration: vars.session_duration_min + " min",
  generated: new Date().toISOString()
};

const summaryText = "Retro: " + artifact.title + "\\n" +
  "Format: " + formatName + " (" + vars.columns_list + ")\\n" +
  "Participants: " + artifact.participants + "\\n" +
  "Items: " + artifact.items_collected + " → " + artifact.themes_identified + " themes\\n" +
  "Top action items ready for Asana creation.";

return {
  retro_artifact: JSON.stringify(artifact),
  artifact_summary: summaryText,
  retro_title: artifact.title
};`,
          outputVariables: [
            { name: "retro_artifact", type: "string" },
            { name: "artifact_summary", type: "string" },
            { name: "retro_title", type: "string" },
          ],
        },
      }),

      // 9. Follow-up automation Asana API
      n("9", "hitApi", "Create Retro Follow-Up Tasks in Asana", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "data.name", value: "[Retro Follow-up] {retro_title}" },
            {
              key: "data.notes",
              value: "{artifact_summary}\\n\\nFull artifact: {retro_artifact}",
            },
            { key: "data.projects", value: "[{asana_project_gid}]" },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "followup_task_id",
              type: "string",
            },
            {
              name: "data.permalink_url",
              customName: "followup_task_url",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1496. AMAZON REVIEW MONITOR (8 nodes)
  // ===================================================================

  "Amazon Review Monitor": {
    summary:
      "Runs 3x daily on schedule, scrapes recent reviews via API, analyzes sentiment with AI, checks rating thresholds with a condition, sends negative review alerts to Slack, drafts review responses with AI, logs trends to a Sheets dashboard, and sends a weekly review digest email.",
    nodes: [
      trigger("Schedule: Amazon review check (3x daily)", "schedule", {
        schedule: "0 8,14,20 * * *",
        howItStarts:
          "Runs three times daily at 8 AM, 2 PM, and 8 PM to monitor Amazon product reviews, detect sentiment trends, and alert on negative reviews requiring response",
        setup:
          "Configure Amazon product review API or scraping service credentials. Set OpenAI API key for sentiment analysis. Configure Slack webhook for alerts. Set Google Sheets for trend tracking. Set email for weekly digests.",
        inputFields: [
          { name: "product_asin", type: "string", example: "B08N5WRWNW" },
          {
            name: "product_name",
            type: "string",
            example: "Wireless Noise-Cancelling Headphones",
          },
          { name: "brand_email", type: "string", example: "reviews@brand.com" },
          { name: "alert_threshold", type: "string", example: "3" },
        ],
      }),

      // 2. Review scraper API
      n("2", "hitApi", "Scrape Recent Amazon Reviews", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.rainforestapi.com/request?api_key={rainforest_api_key}&type=reviews&amazon_domain=amazon.com&asin={product_asin}&sort_by=most_recent",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "reviews", customName: "recent_reviews", type: "string" },
            {
              name: "summary.rating",
              customName: "avg_rating",
              type: "number",
            },
            {
              name: "summary.total_reviews",
              customName: "total_review_count",
              type: "number",
            },
            {
              name: "reviews.length",
              customName: "new_review_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI sentiment analyzer
      aiNode(
        "3",
        "AI Analyze Review Sentiment and Themes",
        520,
        "You are a product review analyst. Analyze the batch of recent Amazon reviews for sentiment, common themes, product issues, and praise patterns. Identify reviews that need urgent response. Calculate sentiment distribution. Respond JSON: {overall_sentiment: string, sentiment_score: number, positive_themes: string, negative_themes: string, urgent_reviews: string, urgent_count: number, product_issues: string, praise_highlights: string, avg_stars: number}",
        "Product: {product_name} (ASIN: {product_asin}). Reviews: {recent_reviews}. Total reviews: {total_review_count}. Average rating: {avg_rating}. Analyze sentiment and identify actionable themes.",
        [
          {
            field: "overall_sentiment",
            as: "overall_sentiment",
            type: "string",
          },
          { field: "sentiment_score", as: "sentiment_score", type: "number" },
          { field: "positive_themes", as: "positive_themes", type: "string" },
          { field: "negative_themes", as: "negative_themes", type: "string" },
          { field: "urgent_reviews", as: "urgent_reviews", type: "string" },
          { field: "urgent_count", as: "urgent_count", type: "number" },
          { field: "product_issues", as: "product_issues", type: "string" },
          {
            field: "praise_highlights",
            as: "praise_highlights",
            type: "string",
          },
          { field: "avg_stars", as: "avg_stars", type: "number" },
        ],
      ),

      // 4. Rating threshold alert condition
      n("4", "condition", "Negative Reviews Requiring Attention?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "urgent_count", operator: "gt", value: "0" },
          ],
        },
      }),

      // 5. Negative review alert Slack (true path)
      slackNode(
        "5",
        "Alert Team to Negative Reviews on Slack",
        1000,
        ":warning: *Negative Review Alert — {product_name}*\\n\\n*Urgent Reviews:* {urgent_count}\\n*Overall Sentiment:* {overall_sentiment} (score: {sentiment_score}/100)\\n*Average Stars:* {avg_stars}/5\\n\\n*Product Issues:*\\n{product_issues}\\n\\n*Negative Themes:*\\n{negative_themes}\\n\\n*Reviews Needing Response:*\\n{urgent_reviews}\\n\\nPlease draft responses ASAP. AI response suggestions incoming.",
      ),

      // 6. AI review response drafter
      aiNode(
        "6",
        "AI Draft Responses to Negative Reviews",
        1240,
        "You are a brand reputation manager. Draft professional, empathetic responses to the urgent negative reviews. Each response should acknowledge the issue, apologize sincerely, offer a resolution path, and invite the customer to connect directly. Maintain brand voice — helpful, not defensive. Respond JSON: {response_drafts: string, response_count: number, escalation_needed: string, resolution_suggestions: string}",
        "Product: {product_name}. Urgent reviews: {urgent_reviews}. Product issues: {product_issues}. Negative themes: {negative_themes}. Draft thoughtful responses for each urgent review.",
        [
          { field: "response_drafts", as: "response_drafts", type: "string" },
          { field: "response_count", as: "response_count", type: "number" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
          {
            field: "resolution_suggestions",
            as: "resolution_suggestions",
            type: "string",
          },
        ],
      ),

      // 7. Review trend dashboard Sheets
      sheetsNode(
        "7",
        "Log Review Trends to Dashboard",
        1480,
        "ReviewTrends",
        '[["{product_asin}","{product_name}","{avg_rating}","{total_review_count}","{new_review_count}","{overall_sentiment}","{sentiment_score}","{urgent_count}","{positive_themes}","{negative_themes}","{product_issues}"]]',
      ),

      // 8. Weekly review digest email
      emailNode(
        "8",
        "Send Review Monitoring Digest",
        1720,
        "{brand_email}",
        "Review Monitor: {product_name} — {overall_sentiment} ({avg_stars}/5 stars)",
        "<h2>Review Monitor: {product_name}</h2><p><strong>ASIN:</strong> {product_asin} | <strong>Total Reviews:</strong> {total_review_count} | <strong>Average:</strong> {avg_rating}/5</p><h3>Sentiment Analysis</h3><p><strong>Overall:</strong> {overall_sentiment} (score: {sentiment_score}/100)</p><p><strong>New Reviews:</strong> {new_review_count} | <strong>Urgent:</strong> {urgent_count}</p><h3>Themes</h3><p><strong>Positive:</strong> {positive_themes}</p><p><strong>Negative:</strong> {negative_themes}</p><p><strong>Product Issues:</strong> {product_issues}</p><p><strong>Praise:</strong> {praise_highlights}</p><h3>Response Drafts</h3><p>{response_drafts}</p><p><strong>Escalation Needed:</strong> {escalation_needed}</p><p><strong>Resolution Suggestions:</strong> {resolution_suggestions}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1497. AUTONOMOUS WEB SCRAPING AGENT (9 nodes)
  // ===================================================================

  "Autonomous Web Scraping Agent": {
    summary:
      "Receives research tasks via webhook, extracts the research brief with AI, plans URL discovery and crawling with runCode, scrapes data via an intelligent scraper API, implements anti-detection rate limiting with runCode, extracts and structures data with runCode, validates data quality with a condition, analyzes results with AI, and delivers findings via email.",
    nodes: [
      trigger("Webhook: Research scraping task", "webhook_app", {
        source: "research_platform",
        event: "research.scraping_requested",
        howItStarts:
          "Fires when a researcher submits a web scraping task with target URLs, data fields to extract, and output format requirements",
        setup:
          "Configure webhook from research platform. Set OpenAI API key for analysis. Configure web scraping API (ScrapingBee/Bright Data). Set email for results delivery.",
        inputFields: [],
      }),

      // 2. AI extract research brief
      aiNode(
        "2",
        "AI Extract Research and Scraping Brief",
        280,
        "You are a web research coordinator. Extract the scraping task details from the raw webhook payload. Find target URLs or domains, data fields to extract, output format, frequency, and any specific selectors or patterns to follow regardless of field naming. Respond JSON: {target_urls: string, data_fields: string, output_format: string, scraping_depth: string, frequency: string, researcher_email: string, project_name: string, max_pages: number}",
        "Raw webhook payload: {webhook_payload}. Extract all web scraping task parameters regardless of payload structure.",
        [
          { field: "target_urls", as: "target_urls", type: "string" },
          { field: "data_fields", as: "data_fields", type: "string" },
          { field: "output_format", as: "output_format", type: "string" },
          { field: "scraping_depth", as: "scraping_depth", type: "string" },
          { field: "frequency", as: "frequency", type: "string" },
          { field: "researcher_email", as: "researcher_email", type: "string" },
          { field: "project_name", as: "project_name", type: "string" },
          { field: "max_pages", as: "max_pages", type: "number" },
        ],
      ),

      // 3. URL discovery crawl planner runCode
      n("3", "runCode", "Plan Crawl Strategy and URL Queue", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Plan the crawling strategy based on the research brief
const targetUrls = (vars.target_urls || "").split(",").map(u => u.trim()).filter(Boolean);
const depth = (vars.scraping_depth || "shallow").toLowerCase();
const maxPages = parseInt(vars.max_pages || "50", 10);
const fields = (vars.data_fields || "").split(",").map(f => f.trim());

// Build URL queue with priority
const urlQueue = targetUrls.map((url, i) => ({
  url: url,
  priority: i === 0 ? "high" : "medium",
  depth: depth === "deep" ? 3 : depth === "medium" ? 2 : 1,
  status: "pending"
}));

// Determine scraping approach per domain
const domains = [...new Set(targetUrls.map(u => { try { return new URL(u).hostname; } catch(e) { return u; } }))];
const batchSize = Math.min(5, Math.ceil(maxPages / 10));
const estimatedRequests = Math.min(maxPages, urlQueue.length * (depth === "deep" ? 10 : depth === "medium" ? 5 : 1));
const estimatedTime = Math.ceil(estimatedRequests * 2.5);

return {
  url_queue_json: JSON.stringify(urlQueue),
  queue_size: urlQueue.length,
  unique_domains: domains.join(", "),
  domain_count: domains.length,
  batch_size: batchSize,
  estimated_requests: estimatedRequests,
  estimated_time_sec: estimatedTime,
  extraction_fields: fields.join(", "),
  crawl_strategy: depth + " crawl across " + domains.length + " domains"
};`,
          outputVariables: [
            { name: "url_queue_json", type: "string" },
            { name: "queue_size", type: "number" },
            { name: "unique_domains", type: "string" },
            { name: "domain_count", type: "number" },
            { name: "batch_size", type: "number" },
            { name: "estimated_requests", type: "number" },
            { name: "estimated_time_sec", type: "number" },
            { name: "extraction_fields", type: "string" },
            { name: "crawl_strategy", type: "string" },
          ],
        },
      }),

      // 4. Intelligent scraper API
      n("4", "hitApi", "Execute Web Scraping via ScrapingBee", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.scrapingbee.com/api/v1/",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{scrapingbee_api_key}" },
            { key: "url", value: "{target_urls}" },
            { key: "render_js", value: "true" },
            { key: "premium_proxy", value: "true" },
            { key: "country_code", value: "us" },
            { key: "extract_rules", value: "{data_fields}" },
            { key: "block_ads", value: "true" },
          ],
          variables: [
            { name: "body", customName: "raw_scraped_data", type: "string" },
            {
              name: "headers.cost-credit",
              customName: "credits_used",
              type: "number",
            },
            {
              name: "status_code",
              customName: "scrape_status",
              type: "number",
            },
          ],
        },
      }),

      // 5. Anti-detection rate limiter runCode
      n("5", "runCode", "Apply Anti-Detection and Rate Limiting", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Apply rate limiting and anti-detection measures
const rawData = vars.raw_scraped_data || "";
const creditsUsed = parseInt(vars.credits_used || "0", 10);
const status = parseInt(vars.scrape_status || "200", 10);
const estimatedRequests = parseInt(vars.estimated_requests || "1", 10);

// Rate limiting calculations
const requestsPerMinute = 10;
const cooldownSec = 3;
const totalCreditsEstimate = creditsUsed * estimatedRequests;
const timeNeeded = Math.ceil(estimatedRequests / requestsPerMinute);

// Data size check
const dataSize = rawData.length;
const isBlocked = status === 403 || status === 429 || dataSize < 100;
const dataValid = !isBlocked && dataSize > 500;

return {
  rate_limit_status: isBlocked ? "blocked" : "ok",
  data_valid: dataValid ? "yes" : "no",
  data_size_bytes: dataSize,
  credits_consumed: creditsUsed,
  total_credits_estimate: totalCreditsEstimate,
  requests_remaining: estimatedRequests - 1,
  cooldown_seconds: cooldownSec,
  scrape_health: dataValid ? "healthy" : isBlocked ? "blocked — retry with proxy rotation" : "low data — check selectors"
};`,
          outputVariables: [
            { name: "rate_limit_status", type: "string" },
            { name: "data_valid", type: "string" },
            { name: "data_size_bytes", type: "number" },
            { name: "credits_consumed", type: "number" },
            { name: "total_credits_estimate", type: "number" },
            { name: "requests_remaining", type: "number" },
            { name: "cooldown_seconds", type: "number" },
            { name: "scrape_health", type: "string" },
          ],
        },
      }),

      // 6. Data extractor structurer runCode
      n("6", "runCode", "Extract and Structure Scraped Data", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract and structure the raw scraped data into the requested format
const rawData = vars.raw_scraped_data || "";
const fields = (vars.extraction_fields || "").split(",").map(f => f.trim());
const format = (vars.output_format || "json").toLowerCase();

// Parse and structure data
let records = [];
try {
  const parsed = typeof rawData === "string" && rawData.startsWith("[") ? JSON.parse(rawData) : [{ raw: rawData }];
  records = Array.isArray(parsed) ? parsed : [parsed];
} catch(e) {
  records = [{ raw_text: rawData.substring(0, 5000) }];
}

const recordCount = records.length;
const fieldsExtracted = fields.filter(f => records.some(r => r[f] !== undefined)).length;
const completeness = fields.length > 0 ? Math.round((fieldsExtracted / fields.length) * 100) : 0;
const qualityScore = completeness >= 80 ? "high" : completeness >= 50 ? "medium" : "low";

return {
  structured_data: JSON.stringify(records.slice(0, 100)),
  record_count: recordCount,
  fields_extracted: fieldsExtracted,
  fields_expected: fields.length,
  completeness_pct: completeness,
  data_quality: qualityScore,
  extraction_summary: recordCount + " records, " + fieldsExtracted + "/" + fields.length + " fields, " + completeness + "% complete"
};`,
          outputVariables: [
            { name: "structured_data", type: "string" },
            { name: "record_count", type: "number" },
            { name: "fields_extracted", type: "number" },
            { name: "fields_expected", type: "number" },
            { name: "completeness_pct", type: "number" },
            { name: "data_quality", type: "string" },
            { name: "extraction_summary", type: "string" },
          ],
        },
      }),

      // 7. Data quality validator condition
      n("7", "condition", "Data Quality Meets Threshold?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "data_quality", operator: "neq", value: "low" },
          ],
        },
      }),

      // 8. AI data analyzer (true path — quality acceptable)
      aiNode(
        "8",
        "AI Analyze Scraped Research Data",
        1720,
        "You are a data analyst. Analyze the structured scraped data and provide insights, patterns, anomalies, and a summary relevant to the research project. Highlight key findings and data quality observations. Respond JSON: {key_findings: string, data_patterns: string, anomalies_detected: string, research_summary: string, data_recommendations: string, confidence_level: string}",
        "Project: {project_name}. Crawl strategy: {crawl_strategy}. Data quality: {data_quality}. Records: {record_count}. Completeness: {completeness_pct}%. Structured data: {structured_data}. Analyze and summarize findings.",
        [
          { field: "key_findings", as: "key_findings", type: "string" },
          { field: "data_patterns", as: "data_patterns", type: "string" },
          {
            field: "anomalies_detected",
            as: "anomalies_detected",
            type: "string",
          },
          { field: "research_summary", as: "research_summary", type: "string" },
          {
            field: "data_recommendations",
            as: "data_recommendations",
            type: "string",
          },
          { field: "confidence_level", as: "confidence_level", type: "string" },
        ],
      ),

      // 9. Results delivery email
      emailNode(
        "9",
        "Deliver Scraping Results via Email",
        1960,
        "{researcher_email}",
        "Scraping Results: {project_name} — {record_count} Records ({data_quality} Quality)",
        "<h2>Web Scraping Results: {project_name}</h2><p><strong>Strategy:</strong> {crawl_strategy} | <strong>Domains:</strong> {unique_domains}</p><p><strong>Records:</strong> {record_count} | <strong>Quality:</strong> {data_quality} ({completeness_pct}% complete)</p><p><strong>Credits Used:</strong> {credits_consumed} | <strong>Scrape Health:</strong> {scrape_health}</p><hr><h3>Key Findings</h3><p>{key_findings}</p><h3>Data Patterns</h3><p>{data_patterns}</p><h3>Anomalies</h3><p>{anomalies_detected}</p><h3>Research Summary</h3><p>{research_summary}</p><p><strong>Confidence:</strong> {confidence_level}</p><h3>Recommendations</h3><p>{data_recommendations}</p><p><strong>Extraction Summary:</strong> {extraction_summary}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "9", "output_2"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1498. BRAND MENTION MONITOR (8 nodes)
  // ===================================================================

  "Brand Mention Monitor": {
    summary:
      "Runs hourly on schedule, searches Twitter/X for brand mentions via API, scans Reddit for brand discussions via API, classifies sentiment with AI, checks for negative mentions with a condition, detects influencers with runCode, logs mentions to a Sheets database, and sends a daily brand health report via email.",
    nodes: [
      trigger("Schedule: Hourly brand mention scan", "schedule", {
        schedule: "0 * * * *",
        howItStarts:
          "Runs every hour to monitor brand mentions across Twitter/X and Reddit, analyzing sentiment and detecting influencer engagement or reputation risks",
        setup:
          "Configure Twitter API v2 bearer token for search. Configure Reddit API credentials. Set OpenAI API key for sentiment analysis. Set Slack for alerts. Set Google Sheets for mention logging. Set email for daily reports.",
        inputFields: [
          { name: "brand_name", type: "string", example: "OmniEngage" },
          {
            name: "brand_keywords",
            type: "string",
            example: "OmniEngage, omni engage, @omniengage",
          },
          {
            name: "brand_email",
            type: "string",
            example: "marketing@omniengage.com",
          },
        ],
      }),

      // 2. Twitter X search API
      n("2", "hitApi", "Search Twitter/X for Brand Mentions", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.twitter.com/2/tweets/search/recent?query={brand_keywords}&max_results=100&tweet.fields=author_id,created_at,public_metrics,lang&expansions=author_id&user.fields=name,username,public_metrics",
          headers: [
            { key: "Authorization", value: "Bearer {twitter_bearer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "twitter_mentions", type: "string" },
            {
              name: "meta.result_count",
              customName: "twitter_mention_count",
              type: "number",
            },
            {
              name: "includes.users",
              customName: "twitter_authors",
              type: "string",
            },
          ],
        },
      }),

      // 3. Reddit scanner API
      n("3", "hitApi", "Search Reddit for Brand Discussions", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://oauth.reddit.com/search?q={brand_keywords}&sort=new&limit=50&t=hour",
          headers: [
            { key: "Authorization", value: "Bearer {reddit_access_token}" },
            { key: "User-Agent", value: "BrandMonitor/1.0" },
          ],
          body: [],
          variables: [
            {
              name: "data.children",
              customName: "reddit_mentions",
              type: "string",
            },
            {
              name: "data.dist",
              customName: "reddit_mention_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI sentiment classifier
      aiNode(
        "4",
        "AI Classify Brand Mention Sentiment",
        760,
        "You are a brand sentiment analyst. Classify each brand mention from Twitter and Reddit by sentiment (positive, negative, neutral), urgency (high, medium, low), and topic category. Identify mentions that require immediate response. Calculate overall brand sentiment score. Respond JSON: {overall_sentiment: string, sentiment_score: number, positive_count: number, negative_count: number, neutral_count: number, urgent_mentions: string, urgent_count: number, top_topics: string, brand_perception: string}",
        "Brand: {brand_name}. Twitter mentions ({twitter_mention_count}): {twitter_mentions}. Reddit mentions ({reddit_mention_count}): {reddit_mentions}. Classify all mentions by sentiment and urgency.",
        [
          {
            field: "overall_sentiment",
            as: "overall_sentiment",
            type: "string",
          },
          { field: "sentiment_score", as: "sentiment_score", type: "number" },
          { field: "positive_count", as: "positive_count", type: "number" },
          { field: "negative_count", as: "negative_count", type: "number" },
          { field: "neutral_count", as: "neutral_count", type: "number" },
          { field: "urgent_mentions", as: "urgent_mentions", type: "string" },
          { field: "urgent_count", as: "urgent_count", type: "number" },
          { field: "top_topics", as: "top_topics", type: "string" },
          { field: "brand_perception", as: "brand_perception", type: "string" },
        ],
      ),

      // 5. Negative mention alert condition
      n("5", "condition", "Urgent Negative Mentions Detected?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "urgent_count", operator: "gt", value: "0" },
          ],
        },
      }),

      // 6. Influencer detection runCode
      n("6", "runCode", "Detect Influencer Mentions and Reach", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Detect influencer mentions and calculate reach
const twitterAuthors = JSON.parse(vars.twitter_authors || "[]");
const twitterCount = parseInt(vars.twitter_mention_count || "0", 10);
const redditCount = parseInt(vars.reddit_mention_count || "0", 10);
const totalMentions = twitterCount + redditCount;

// Find high-follower authors (influencers)
const influencers = twitterAuthors
  .filter(a => a.public_metrics && a.public_metrics.followers_count > 5000)
  .map(a => ({
    name: a.name || a.username,
    username: a.username,
    followers: a.public_metrics.followers_count
  }))
  .sort((a, b) => b.followers - a.followers);

const totalReach = twitterAuthors.reduce((sum, a) => sum + (a.public_metrics ? a.public_metrics.followers_count : 0), 0);
const influencerReach = influencers.reduce((sum, i) => sum + i.followers, 0);

return {
  total_mentions: totalMentions,
  total_reach: totalReach,
  influencer_count: influencers.length,
  influencer_list: influencers.slice(0, 5).map(i => "@" + i.username + " (" + i.followers + " followers)").join("; ") || "none detected",
  influencer_reach: influencerReach,
  reach_percentage: totalReach > 0 ? Math.round((influencerReach / totalReach) * 100) : 0,
  mention_velocity: totalMentions + " mentions this hour",
  scan_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "total_mentions", type: "number" },
            { name: "total_reach", type: "number" },
            { name: "influencer_count", type: "number" },
            { name: "influencer_list", type: "string" },
            { name: "influencer_reach", type: "number" },
            { name: "reach_percentage", type: "number" },
            { name: "mention_velocity", type: "string" },
            { name: "scan_timestamp", type: "string" },
          ],
        },
      }),

      // 7. Mention database Sheets
      sheetsNode(
        "7",
        "Log Brand Mentions to Sheets Database",
        1480,
        "BrandMentions",
        '[["{scan_timestamp}","{brand_name}","{total_mentions}","{twitter_mention_count}","{reddit_mention_count}","{overall_sentiment}","{sentiment_score}","{positive_count}","{negative_count}","{urgent_count}","{influencer_count}","{total_reach}","{top_topics}"]]',
      ),

      // 8. Daily brand health report email
      emailNode(
        "8",
        "Send Brand Health Report via Email",
        1720,
        "{brand_email}",
        "Brand Monitor: {brand_name} — {overall_sentiment} ({total_mentions} mentions, {sentiment_score}/100)",
        "<h2>Brand Health Report: {brand_name}</h2><p><strong>Scan Time:</strong> {scan_timestamp} | <strong>Velocity:</strong> {mention_velocity}</p><h3>Sentiment Overview</h3><p><strong>Overall:</strong> {overall_sentiment} (score: {sentiment_score}/100)</p><p>Positive: {positive_count} | Negative: {negative_count} | Neutral: {neutral_count}</p><p><strong>Brand Perception:</strong> {brand_perception}</p><h3>Mentions Breakdown</h3><p><strong>Twitter/X:</strong> {twitter_mention_count} | <strong>Reddit:</strong> {reddit_mention_count} | <strong>Total Reach:</strong> {total_reach}</p><h3>Top Topics</h3><p>{top_topics}</p><h3>Influencer Mentions</h3><p>{influencer_list}</p><p><strong>Influencer Reach:</strong> {influencer_reach} ({reach_percentage}% of total)</p><h3>Urgent Mentions ({urgent_count})</h3><p>{urgent_mentions}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1499. COMPETITIVE PRICING ALERT (8 nodes)
  // ===================================================================

  "Competitive Pricing Alert": {
    summary:
      "Runs daily on schedule, scrapes competitor prices via API, normalizes price data with runCode, compares prices with AI, detects undercuts with a condition, sends urgent price alerts to Slack, tracks price history in Sheets, and sends a weekly competitive pricing report via email.",
    nodes: [
      trigger("Schedule: Daily competitor price check", "schedule", {
        schedule: "0 7 * * *",
        howItStarts:
          "Runs every morning at 7 AM to scrape competitor product prices, compare against own pricing, and alert when competitors undercut on key products",
        setup:
          "Configure web scraping API for competitor sites. Set OpenAI API key for analysis. Configure Slack webhook for urgent alerts. Set Google Sheets for price tracking. Set email for weekly reports.",
        inputFields: [
          {
            name: "product_catalog_url",
            type: "string",
            example: "https://api.internal.com/products/catalog",
          },
          {
            name: "competitor_urls",
            type: "string",
            example:
              "https://competitor1.com/pricing, https://competitor2.com/pricing",
          },
          {
            name: "pricing_team_email",
            type: "string",
            example: "pricing@company.com",
          },
        ],
      }),

      // 2. Competitor price scraper API
      n("2", "hitApi", "Scrape Competitor Pricing Pages", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.scrapingbee.com/api/v1/",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{scrapingbee_api_key}" },
            { key: "url", value: "{competitor_urls}" },
            { key: "render_js", value: "true" },
            {
              key: "extract_rules",
              value:
                '{"prices":{"selector":".price","type":"list"},"products":{"selector":".product-name","type":"list"}}',
            },
          ],
          variables: [
            { name: "body", customName: "competitor_raw_data", type: "string" },
            {
              name: "status_code",
              customName: "scrape_status",
              type: "number",
            },
          ],
        },
      }),

      // 3. Price data normalizer runCode
      n("3", "runCode", "Normalize and Compare Price Data", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize competitor pricing data for comparison
const rawData = vars.competitor_raw_data || "{}";
let parsed;
try { parsed = JSON.parse(rawData); } catch(e) { parsed = { prices: [], products: [] }; }

const prices = parsed.prices || [];
const products = parsed.products || [];

// Build normalized price map
const priceMap = [];
for (let i = 0; i < Math.min(prices.length, products.length); i++) {
  const priceStr = (prices[i] || "").replace(/[^0-9.]/g, "");
  const price = parseFloat(priceStr) || 0;
  if (price > 0) {
    priceMap.push({
      product: products[i] || "Product " + (i + 1),
      competitor_price: price,
      source: "competitor"
    });
  }
}

const avgCompetitorPrice = priceMap.length > 0 ? Math.round(priceMap.reduce((s, p) => s + p.competitor_price, 0) / priceMap.length * 100) / 100 : 0;
const lowestPrice = priceMap.length > 0 ? Math.min(...priceMap.map(p => p.competitor_price)) : 0;
const highestPrice = priceMap.length > 0 ? Math.max(...priceMap.map(p => p.competitor_price)) : 0;

return {
  normalized_prices: JSON.stringify(priceMap),
  products_tracked: priceMap.length,
  avg_competitor_price: avgCompetitorPrice,
  lowest_competitor_price: lowestPrice,
  highest_competitor_price: highestPrice,
  price_range: lowestPrice + " - " + highestPrice,
  scan_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "normalized_prices", type: "string" },
            { name: "products_tracked", type: "number" },
            { name: "avg_competitor_price", type: "number" },
            { name: "lowest_competitor_price", type: "number" },
            { name: "highest_competitor_price", type: "number" },
            { name: "price_range", type: "string" },
            { name: "scan_date", type: "string" },
          ],
        },
      }),

      // 4. AI price comparator
      aiNode(
        "4",
        "AI Compare Pricing and Identify Opportunities",
        1000,
        "You are a competitive pricing analyst. Compare the competitor pricing data against market norms. Identify products where competitors are significantly cheaper (undercuts), products where you have pricing power, and market trends. Flag any suspicious pricing that might indicate clearance or errors. Respond JSON: {undercut_products: string, undercut_count: number, pricing_opportunities: string, market_trend: string, suspicious_prices: string, recommended_actions: string, competitive_position: string}",
        "Products tracked: {products_tracked}. Average competitor price: {avg_competitor_price}. Price range: {price_range}. Detailed prices: {normalized_prices}. Analyze competitive positioning and identify price risks.",
        [
          {
            field: "undercut_products",
            as: "undercut_products",
            type: "string",
          },
          { field: "undercut_count", as: "undercut_count", type: "number" },
          {
            field: "pricing_opportunities",
            as: "pricing_opportunities",
            type: "string",
          },
          { field: "market_trend", as: "market_trend", type: "string" },
          {
            field: "suspicious_prices",
            as: "suspicious_prices",
            type: "string",
          },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          {
            field: "competitive_position",
            as: "competitive_position",
            type: "string",
          },
        ],
      ),

      // 5. Undercut detector condition
      n("5", "condition", "Competitor Undercuts Detected?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "undercut_count", operator: "gt", value: "0" },
          ],
        },
      }),

      // 6. Urgent price alert Slack (true path)
      slackNode(
        "6",
        "Alert Pricing Team About Undercuts",
        1480,
        ":rotating_light: *Competitive Pricing Alert*\\n\\n*{undercut_count} product(s) undercut by competitors!*\\n\\n*Products:*\\n{undercut_products}\\n\\n*Market Trend:* {market_trend}\\n*Competitive Position:* {competitive_position}\\n\\n*Recommended Actions:*\\n{recommended_actions}\\n\\n*Suspicious Prices:* {suspicious_prices}",
      ),

      // 7. Price history tracker Sheets
      sheetsNode(
        "7",
        "Log Price Data to History Tracker",
        1720,
        "PriceHistory",
        '[["{scan_date}","{products_tracked}","{avg_competitor_price}","{lowest_competitor_price}","{highest_competitor_price}","{undercut_count}","{competitive_position}","{market_trend}","{recommended_actions}"]]',
      ),

      // 8. Weekly competitive pricing report email
      emailNode(
        "8",
        "Send Competitive Pricing Report",
        1960,
        "{pricing_team_email}",
        "Competitive Pricing Report — {scan_date} ({undercut_count} undercuts, {products_tracked} products)",
        "<h2>Competitive Pricing Report</h2><p><strong>Date:</strong> {scan_date} | <strong>Products Tracked:</strong> {products_tracked}</p><h3>Price Summary</h3><p><strong>Average Competitor Price:</strong> {avg_competitor_price}</p><p><strong>Price Range:</strong> {price_range}</p><p><strong>Competitive Position:</strong> {competitive_position}</p><h3>Undercut Alert ({undercut_count} products)</h3><p>{undercut_products}</p><h3>Pricing Opportunities</h3><p>{pricing_opportunities}</p><h3>Market Trend</h3><p>{market_trend}</p><h3>Suspicious Prices</h3><p>{suspicious_prices}</p><h3>Recommended Actions</h3><p>{recommended_actions}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1500. COMPETITOR PRICE TRACKER (7 nodes)
  // ===================================================================

  "Competitor Price Tracker": {
    summary:
      "Runs daily on schedule, scrapes competitor pricing pages via API, detects price changes with runCode, analyzes changes with AI, routes significant changes through a condition, logs prices to a Sheets database, and sends a team summary email.",
    nodes: [
      trigger("Schedule: Daily competitor price tracker", "schedule", {
        schedule: "0 6 * * *",
        howItStarts:
          "Runs daily at 6 AM to track competitor product prices, detect changes from previous values, and maintain a historical price database",
        setup:
          "Configure web scraping API credentials. Set OpenAI API key for change analysis. Set Google Sheets spreadsheet ID for price database. Set email for team summaries.",
        inputFields: [
          { name: "competitor_name", type: "string", example: "CompetitorCo" },
          {
            name: "competitor_pricing_url",
            type: "string",
            example: "https://competitorco.com/pricing",
          },
          {
            name: "team_email",
            type: "string",
            example: "strategy@company.com",
          },
        ],
      }),

      // 2. Price page scraper API
      n("2", "hitApi", "Scrape Competitor Pricing Page", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.scrapingbee.com/api/v1/",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [
            { key: "api_key", value: "{scrapingbee_api_key}" },
            { key: "url", value: "{competitor_pricing_url}" },
            { key: "render_js", value: "true" },
            {
              key: "extract_rules",
              value:
                '{"plans":{"selector":".pricing-plan","type":"list","output":{"name":{"selector":".plan-name"},"price":{"selector":".plan-price"},"features":{"selector":".plan-features","type":"list"}}}}',
            },
          ],
          variables: [
            {
              name: "body",
              customName: "scraped_pricing_data",
              type: "string",
            },
            {
              name: "status_code",
              customName: "scrape_status_code",
              type: "number",
            },
          ],
        },
      }),

      // 3. Change detector runCode
      n("3", "runCode", "Detect Price Changes from Previous Scan", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse current prices and detect changes
const rawData = vars.scraped_pricing_data || "{}";
let parsed;
try { parsed = JSON.parse(rawData); } catch(e) { parsed = { plans: [] }; }

const plans = parsed.plans || [];
const currentPrices = plans.map(p => ({
  name: p.name || "Unknown Plan",
  price: parseFloat((p.price || "0").replace(/[^0-9.]/g, "")),
  features: Array.isArray(p.features) ? p.features.join("; ") : (p.features || "")
}));

// Detect changes (comparing against stored previous values)
const changes = [];
let hasSignificantChange = "no";
currentPrices.forEach(plan => {
  // In production, previous prices would come from Sheets lookup
  // Here we flag any non-zero price as potentially changed
  if (plan.price > 0) {
    changes.push({
      plan: plan.name,
      current_price: plan.price,
      change_type: "tracked"
    });
  }
});

const totalPlans = currentPrices.length;
const avgPrice = totalPlans > 0 ? Math.round(currentPrices.reduce((s, p) => s + p.price, 0) / totalPlans * 100) / 100 : 0;
const priceList = currentPrices.map(p => p.name + ": $" + p.price).join("; ");

return {
  current_prices_json: JSON.stringify(currentPrices),
  plan_count: totalPlans,
  avg_plan_price: avgPrice,
  price_summary: priceList,
  changes_detected: changes.length > 0 ? "yes" : "no",
  change_details: changes.map(c => c.plan + " at $" + c.current_price).join("; ") || "no changes",
  scan_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "current_prices_json", type: "string" },
            { name: "plan_count", type: "number" },
            { name: "avg_plan_price", type: "number" },
            { name: "price_summary", type: "string" },
            { name: "changes_detected", type: "string" },
            { name: "change_details", type: "string" },
            { name: "scan_timestamp", type: "string" },
          ],
        },
      }),

      // 4. AI change analyzer
      aiNode(
        "4",
        "AI Analyze Pricing Changes and Strategy",
        760,
        "You are a competitive intelligence analyst. Analyze the competitors current pricing structure. Identify pricing strategy patterns, value proposition by tier, and any notable changes. Compare against common SaaS pricing benchmarks. Respond JSON: {pricing_strategy: string, value_analysis: string, strategic_implications: string, notable_observations: string, recommended_response: string, threat_level: string}",
        "Competitor: {competitor_name}. Plans tracked: {plan_count}. Average price: {avg_plan_price}. Price summary: {price_summary}. Changes: {change_details}. Full data: {current_prices_json}. Analyze their pricing strategy.",
        [
          { field: "pricing_strategy", as: "pricing_strategy", type: "string" },
          { field: "value_analysis", as: "value_analysis", type: "string" },
          {
            field: "strategic_implications",
            as: "strategic_implications",
            type: "string",
          },
          {
            field: "notable_observations",
            as: "notable_observations",
            type: "string",
          },
          {
            field: "recommended_response",
            as: "recommended_response",
            type: "string",
          },
          { field: "threat_level", as: "threat_level", type: "string" },
        ],
      ),

      // 5. Change alert condition
      n("5", "condition", "Significant Price Changes Detected?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "changes_detected", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. Price database Sheets
      sheetsNode(
        "6",
        "Log Prices to Historical Database",
        1240,
        "CompetitorPrices",
        '[["{scan_timestamp}","{competitor_name}","{plan_count}","{avg_plan_price}","{price_summary}","{changes_detected}","{change_details}","{pricing_strategy}","{threat_level}"]]',
      ),

      // 7. Team summary email
      emailNode(
        "7",
        "Send Price Tracking Summary to Team",
        1480,
        "{team_email}",
        "{competitor_name} Price Tracker — {plan_count} Plans, Avg ${avg_plan_price} (Threat: {threat_level})",
        "<h2>Competitor Price Tracker: {competitor_name}</h2><p><strong>Scan:</strong> {scan_timestamp} | <strong>Status:</strong> {scrape_status_code}</p><h3>Current Pricing</h3><p>{price_summary}</p><p><strong>Plans:</strong> {plan_count} | <strong>Average:</strong> ${avg_plan_price}</p><h3>Changes</h3><p>{change_details}</p><h3>Strategy Analysis</h3><p><strong>Pricing Strategy:</strong> {pricing_strategy}</p><p><strong>Value Analysis:</strong> {value_analysis}</p><p><strong>Threat Level:</strong> {threat_level}</p><h3>Strategic Implications</h3><p>{strategic_implications}</p><p><strong>Notable:</strong> {notable_observations}</p><h3>Recommended Response</h3><p>{recommended_response}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1501. CUSTOMER FEEDBACK SENTIMENT ANALYZER (8 nodes)
  // ===================================================================

  "Customer Feedback Sentiment Analyzer": {
    summary:
      "Runs daily on schedule, collects feedback from multiple sources via API, preprocesses text with runCode, analyzes sentiment with AI, extracts topics with runCode, detects trends with a condition, logs to a Sheets dashboard, and sends a weekly sentiment report via email.",
    nodes: [
      trigger("Schedule: Daily feedback sentiment analysis", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Runs every morning at 9 AM to collect customer feedback from support tickets, NPS surveys, and app store reviews, then analyze sentiment trends",
        setup:
          "Configure feedback API endpoints (Zendesk, Outgrow Forms, app stores). Set OpenAI API key for analysis. Set Google Sheets for dashboard. Set email for weekly reports.",
        inputFields: [
          { name: "zendesk_subdomain", type: "string", example: "mycompany" },
          { name: "product_name", type: "string", example: "OmniEngage" },
          { name: "cx_team_email", type: "string", example: "cx@company.com" },
        ],
      }),

      // 2. Multi-source feedback collector API
      n("2", "hitApi", "Collect Feedback from Zendesk Tickets", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/search.json?query=type:ticket created>24hours&sort_by=created_at&sort_order=desc",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "results", customName: "feedback_items", type: "string" },
            { name: "count", customName: "feedback_count", type: "number" },
          ],
        },
      }),

      // 3. Text preprocessor runCode
      n("3", "runCode", "Preprocess Feedback Text for Analysis", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Preprocess feedback text — clean, normalize, and prepare for sentiment analysis
const items = JSON.parse(vars.feedback_items || "[]");
const feedbackCount = parseInt(vars.feedback_count || "0", 10);

const cleaned = items.map(item => {
  const text = (item.description || item.subject || item.raw_subject || "").trim();
  // Remove HTML tags, excessive whitespace, and normalize
  const cleanText = text.replace(/<[^>]*>/g, "").replace(/\\s+/g, " ").trim();
  return {
    id: item.id || "unknown",
    text: cleanText.substring(0, 500),
    channel: item.via ? item.via.channel : "unknown",
    priority: item.priority || "normal",
    created: item.created_at || ""
  };
}).filter(item => item.text.length > 10);

const channelBreakdown = {};
cleaned.forEach(item => {
  channelBreakdown[item.channel] = (channelBreakdown[item.channel] || 0) + 1;
});

return {
  cleaned_feedback: JSON.stringify(cleaned),
  processed_count: cleaned.length,
  skipped_count: feedbackCount - cleaned.length,
  channel_breakdown: Object.entries(channelBreakdown).map(([ch, ct]) => ch + ": " + ct).join(", "),
  avg_text_length: cleaned.length > 0 ? Math.round(cleaned.reduce((s, i) => s + i.text.length, 0) / cleaned.length) : 0
};`,
          outputVariables: [
            { name: "cleaned_feedback", type: "string" },
            { name: "processed_count", type: "number" },
            { name: "skipped_count", type: "number" },
            { name: "channel_breakdown", type: "string" },
            { name: "avg_text_length", type: "number" },
          ],
        },
      }),

      // 4. AI sentiment analyzer
      aiNode(
        "4",
        "AI Analyze Feedback Sentiment",
        760,
        "You are a customer experience analyst. Analyze the preprocessed feedback for sentiment distribution, emotional intensity, and customer satisfaction indicators. Identify the top positive and negative themes. Flag any feedback indicating churn risk or urgent issues. Respond JSON: {sentiment_distribution: string, avg_sentiment_score: number, positive_pct: number, negative_pct: number, neutral_pct: number, top_positive_theme: string, top_negative_theme: string, churn_risk_signals: string, urgent_issues: string, customer_satisfaction_trend: string}",
        "Product: {product_name}. Feedback count: {processed_count}. Channels: {channel_breakdown}. Feedback data: {cleaned_feedback}. Analyze sentiment across all feedback.",
        [
          {
            field: "sentiment_distribution",
            as: "sentiment_distribution",
            type: "string",
          },
          {
            field: "avg_sentiment_score",
            as: "avg_sentiment_score",
            type: "number",
          },
          { field: "positive_pct", as: "positive_pct", type: "number" },
          { field: "negative_pct", as: "negative_pct", type: "number" },
          { field: "neutral_pct", as: "neutral_pct", type: "number" },
          {
            field: "top_positive_theme",
            as: "top_positive_theme",
            type: "string",
          },
          {
            field: "top_negative_theme",
            as: "top_negative_theme",
            type: "string",
          },
          {
            field: "churn_risk_signals",
            as: "churn_risk_signals",
            type: "string",
          },
          { field: "urgent_issues", as: "urgent_issues", type: "string" },
          {
            field: "customer_satisfaction_trend",
            as: "customer_satisfaction_trend",
            type: "string",
          },
        ],
      ),

      // 5. Topic extractor runCode
      n("5", "runCode", "Extract and Rank Feedback Topics", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract and rank topics from sentiment analysis
const posTheme = vars.top_positive_theme || "";
const negTheme = vars.top_negative_theme || "";
const positivePct = parseFloat(vars.positive_pct || "0");
const negativePct = parseFloat(vars.negative_pct || "0");
const avgScore = parseFloat(vars.avg_sentiment_score || "50");
const processedCount = parseInt(vars.processed_count || "0", 10);

// Trend detection
const sentimentHealthy = avgScore >= 60;
const negativeTrending = negativePct > 30;
const trendDirection = avgScore >= 70 ? "improving" : avgScore >= 50 ? "stable" : "declining";
const alertLevel = negativeTrending ? "high" : negativePct > 20 ? "medium" : "low";

return {
  trend_direction: trendDirection,
  alert_level: alertLevel,
  sentiment_healthy: sentimentHealthy ? "yes" : "no",
  negative_trending: negativeTrending ? "yes" : "no",
  topic_summary: "Top positive: " + posTheme + ". Top negative: " + negTheme,
  health_score: Math.round(avgScore),
  analysis_date: new Date().toISOString().split("T")[0],
  feedback_volume: processedCount + " items analyzed"
};`,
          outputVariables: [
            { name: "trend_direction", type: "string" },
            { name: "alert_level", type: "string" },
            { name: "sentiment_healthy", type: "string" },
            { name: "negative_trending", type: "string" },
            { name: "topic_summary", type: "string" },
            { name: "health_score", type: "number" },
            { name: "analysis_date", type: "string" },
            { name: "feedback_volume", type: "string" },
          ],
        },
      }),

      // 6. Trend detector condition
      n("6", "condition", "Negative Sentiment Trending Up?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "negative_trending", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Sentiment dashboard Sheets
      sheetsNode(
        "7",
        "Log Sentiment Data to Dashboard",
        1480,
        "SentimentDashboard",
        '[["{analysis_date}","{product_name}","{processed_count}","{avg_sentiment_score}","{positive_pct}","{negative_pct}","{neutral_pct}","{trend_direction}","{alert_level}","{top_positive_theme}","{top_negative_theme}","{churn_risk_signals}"]]',
      ),

      // 8. Weekly sentiment report email
      emailNode(
        "8",
        "Send Sentiment Analysis Report",
        1720,
        "{cx_team_email}",
        "Feedback Sentiment: {product_name} — {trend_direction} (Score: {health_score}/100, Alert: {alert_level})",
        "<h2>Customer Feedback Sentiment Report: {product_name}</h2><p><strong>Date:</strong> {analysis_date} | <strong>Volume:</strong> {feedback_volume} | <strong>Channels:</strong> {channel_breakdown}</p><h3>Sentiment Overview</h3><p><strong>Health Score:</strong> {health_score}/100 | <strong>Trend:</strong> {trend_direction} | <strong>Alert Level:</strong> {alert_level}</p><p>Positive: {positive_pct}% | Negative: {negative_pct}% | Neutral: {neutral_pct}%</p><h3>Key Topics</h3><p>{topic_summary}</p><p><strong>Satisfaction Trend:</strong> {customer_satisfaction_trend}</p><h3>Positive Highlights</h3><p>{top_positive_theme}</p><h3>Areas of Concern</h3><p>{top_negative_theme}</p><h3>Churn Risk Signals</h3><p>{churn_risk_signals}</p><h3>Urgent Issues</h3><p>{urgent_issues}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1502. DATA EXTRACTION AGENT (8 nodes)
  // ===================================================================

  "Data Extraction Agent": {
    summary:
      "Receives extraction tasks via webhook, ingests documents with runCode, performs OCR text extraction with runCode, extracts fields with AI, validates and normalizes data with runCode, scores confidence with a condition, formats output with runCode, and delivers results via email.",
    nodes: [
      trigger("Webhook: Data extraction task", "webhook_app", {
        source: "document_pipeline",
        event: "extraction.task_submitted",
        howItStarts:
          "Fires when a document is submitted for data extraction, including the document content, expected fields, and output format requirements",
        setup:
          "Configure webhook from document upload service. Set OpenAI API key for AI field extraction. Set email for results delivery.",
        inputFields: [],
      }),

      // 2. Document ingestion runCode
      n("2", "runCode", "Ingest and Classify Document", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Ingest document from webhook and classify type
const payload = vars.webhook_payload || "";
let docContent = "";
let docType = "unknown";
let docSize = 0;

try {
  const parsed = JSON.parse(payload);
  docContent = parsed.content || parsed.text || parsed.body || parsed.document || payload;
  docType = parsed.document_type || parsed.type || "unknown";
  docSize = (typeof docContent === "string" ? docContent.length : 0);
} catch(e) {
  docContent = payload;
  docSize = payload.length;
}

// Classify document type by content patterns
if (docType === "unknown") {
  const contentLower = docContent.toLowerCase();
  if (contentLower.includes("invoice") || contentLower.includes("total due")) docType = "invoice";
  else if (contentLower.includes("resume") || contentLower.includes("experience")) docType = "resume";
  else if (contentLower.includes("contract") || contentLower.includes("agreement")) docType = "contract";
  else if (contentLower.includes("receipt") || contentLower.includes("payment")) docType = "receipt";
  else docType = "general";
}

return {
  document_content: docContent.substring(0, 10000),
  document_type: docType,
  document_size: docSize,
  ingestion_timestamp: new Date().toISOString(),
  requires_ocr: docSize < 50 ? "yes" : "no",
  submitter_email: (() => { try { return JSON.parse(payload).email || JSON.parse(payload).submitter_email || "unknown"; } catch(e) { return "unknown"; } })()
};`,
          outputVariables: [
            { name: "document_content", type: "string" },
            { name: "document_type", type: "string" },
            { name: "document_size", type: "number" },
            { name: "ingestion_timestamp", type: "string" },
            { name: "requires_ocr", type: "string" },
            { name: "submitter_email", type: "string" },
          ],
        },
      }),

      // 3. OCR text extraction runCode
      n("3", "runCode", "Extract Text via OCR Processing", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// OCR-style text extraction and cleaning
const content = vars.document_content || "";
const docType = vars.document_type || "general";
const needsOcr = vars.requires_ocr === "yes";

// Clean and normalize extracted text
let cleanedText = content
  .replace(/\\r\\n/g, "\\n")
  .replace(/\\t+/g, " ")
  .replace(/ {2,}/g, " ")
  .trim();

// Extract structured segments
const lines = cleanedText.split("\\n").filter(l => l.trim().length > 0);
const headerLines = lines.slice(0, 5).join(" | ");
const bodyText = lines.slice(5).join("\\n");

return {
  extracted_text: cleanedText.substring(0, 8000),
  text_lines: lines.length,
  header_summary: headerLines,
  body_content: bodyText.substring(0, 6000),
  ocr_performed: needsOcr ? "yes" : "skipped — text already present",
  extraction_quality: lines.length > 10 ? "high" : lines.length > 3 ? "medium" : "low"
};`,
          outputVariables: [
            { name: "extracted_text", type: "string" },
            { name: "text_lines", type: "number" },
            { name: "header_summary", type: "string" },
            { name: "body_content", type: "string" },
            { name: "ocr_performed", type: "string" },
            { name: "extraction_quality", type: "string" },
          ],
        },
      }),

      // 4. AI field extractor
      aiNode(
        "4",
        "AI Extract Structured Fields from Text",
        760,
        "You are a document data extraction specialist. Extract all structured fields from the document text based on its type. For invoices: vendor, date, amounts, line items. For resumes: name, contact, experience, skills. For contracts: parties, dates, terms. For receipts: merchant, date, items, total. Be thorough and extract every identifiable field. Respond JSON: {extracted_fields: string, field_count: number, key_entities: string, dates_found: string, amounts_found: string, names_found: string, confidence_score: number, extraction_notes: string}",
        "Document type: {document_type}. Header: {header_summary}. Full text ({text_lines} lines): {extracted_text}. Extract all structured fields with high precision.",
        [
          { field: "extracted_fields", as: "extracted_fields", type: "string" },
          { field: "field_count", as: "field_count", type: "number" },
          { field: "key_entities", as: "key_entities", type: "string" },
          { field: "dates_found", as: "dates_found", type: "string" },
          { field: "amounts_found", as: "amounts_found", type: "string" },
          { field: "names_found", as: "names_found", type: "string" },
          { field: "confidence_score", as: "confidence_score", type: "number" },
          { field: "extraction_notes", as: "extraction_notes", type: "string" },
        ],
      ),

      // 5. Data validation normalization runCode
      n("5", "runCode", "Validate and Normalize Extracted Data", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate and normalize extracted fields
const fields = vars.extracted_fields || "";
const confidence = parseFloat(vars.confidence_score || "0");
const fieldCount = parseInt(vars.field_count || "0", 10);
const docType = vars.document_type || "general";

// Validation rules by document type
const minFields = { invoice: 5, resume: 4, contract: 4, receipt: 3, general: 2 };
const requiredMin = minFields[docType] || 2;
const hasEnoughFields = fieldCount >= requiredMin;
const highConfidence = confidence >= 70;
const passesValidation = hasEnoughFields && highConfidence;

// Normalize dates and amounts
const dates = (vars.dates_found || "").trim();
const amounts = (vars.amounts_found || "").trim();
const names = (vars.names_found || "").trim();

return {
  validation_passed: passesValidation ? "yes" : "no",
  validation_details: passesValidation ? "All checks passed" : "Issues: " + (!hasEnoughFields ? "insufficient fields (" + fieldCount + "/" + requiredMin + "). " : "") + (!highConfidence ? "low confidence (" + confidence + "%). " : ""),
  normalized_dates: dates || "none found",
  normalized_amounts: amounts || "none found",
  normalized_names: names || "none found",
  quality_grade: confidence >= 90 ? "A" : confidence >= 75 ? "B" : confidence >= 60 ? "C" : "D",
  fields_vs_expected: fieldCount + "/" + requiredMin + " minimum fields"
};`,
          outputVariables: [
            { name: "validation_passed", type: "string" },
            { name: "validation_details", type: "string" },
            { name: "normalized_dates", type: "string" },
            { name: "normalized_amounts", type: "string" },
            { name: "normalized_names", type: "string" },
            { name: "quality_grade", type: "string" },
            { name: "fields_vs_expected", type: "string" },
          ],
        },
      }),

      // 6. Confidence scorer condition
      n("6", "condition", "Extraction Quality Acceptable?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "validation_passed", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 7. Output formatter runCode
      n("7", "runCode", "Format Extraction Results for Delivery", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format the final extraction results
const docType = vars.document_type || "general";
const grade = vars.quality_grade || "N/A";
const confidence = vars.confidence_score || "0";
const fields = vars.extracted_fields || "{}";
const validation = vars.validation_passed === "yes" ? "PASSED" : "NEEDS REVIEW";

const report = {
  document_type: docType,
  quality_grade: grade,
  confidence: confidence + "%",
  validation: validation,
  fields_extracted: parseInt(vars.field_count || "0", 10),
  entities: vars.key_entities || "",
  dates: vars.normalized_dates || "",
  amounts: vars.normalized_amounts || "",
  names: vars.normalized_names || "",
  data: fields,
  generated: new Date().toISOString()
};

return {
  formatted_report: JSON.stringify(report, null, 2),
  report_summary: docType.toUpperCase() + " extraction — Grade " + grade + " (" + confidence + "% confidence) — " + validation,
  delivery_timestamp: report.generated
};`,
          outputVariables: [
            { name: "formatted_report", type: "string" },
            { name: "report_summary", type: "string" },
            { name: "delivery_timestamp", type: "string" },
          ],
        },
      }),

      // 8. Results delivery email
      emailNode(
        "8",
        "Deliver Extraction Results via Email",
        1720,
        "{submitter_email}",
        "Data Extraction Complete: {document_type} — Grade {quality_grade} ({confidence_score}% confidence)",
        "<h2>Data Extraction Results</h2><p><strong>Document Type:</strong> {document_type} | <strong>Quality:</strong> Grade {quality_grade} | <strong>Confidence:</strong> {confidence_score}%</p><p><strong>Validation:</strong> {validation_details}</p><p><strong>Fields Extracted:</strong> {fields_vs_expected}</p><hr><h3>Extracted Data</h3><p>{extracted_fields}</p><h3>Key Entities</h3><p>{key_entities}</p><h3>Dates</h3><p>{normalized_dates}</p><h3>Amounts</h3><p>{normalized_amounts}</p><h3>Names</h3><p>{normalized_names}</p><h3>Notes</h3><p>{extraction_notes}</p><hr><p><strong>Summary:</strong> {report_summary}</p><p><em>Processed at {delivery_timestamp} | OCR: {ocr_performed} | Lines: {text_lines}</em></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1503. IMAGE ANALYSIS AND CLASSIFICATION AGENT (8 nodes)
  // ===================================================================

  "Image Analysis and Classification Agent": {
    summary:
      "Receives image uploads via webhook, preprocesses image metadata with runCode, analyzes images with AI vision, classifies with a runCode engine, evaluates confidence with a condition, enriches metadata in Sheets, routes actions with a second condition, and sends results notification to Slack.",
    nodes: [
      trigger("Webhook: Image upload for analysis", "webhook_app", {
        source: "media_pipeline",
        event: "image.uploaded",
        howItStarts:
          "Fires when an image is uploaded to the media pipeline for automated analysis, classification, and metadata enrichment",
        setup:
          "Configure webhook from media upload service. Set OpenAI API key for vision analysis. Set Google Sheets for metadata logging. Configure Slack for results notification.",
        inputFields: [],
      }),

      // 2. Image preprocessor runCode
      n("2", "runCode", "Preprocess Image Metadata", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Extract image metadata from webhook payload
const payload = vars.webhook_payload || "";
let imageUrl = "";
let fileName = "";
let fileSize = 0;
let mimeType = "";
let uploadedBy = "";

try {
  const parsed = JSON.parse(payload);
  imageUrl = parsed.image_url || parsed.url || parsed.file_url || "";
  fileName = parsed.file_name || parsed.filename || parsed.name || "unknown";
  fileSize = parsed.file_size || parsed.size || 0;
  mimeType = parsed.mime_type || parsed.content_type || "image/jpeg";
  uploadedBy = parsed.uploaded_by || parsed.user_email || parsed.email || "unknown";
} catch(e) {
  imageUrl = payload.trim();
}

const extension = fileName.split(".").pop().toLowerCase();
const isValidImage = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "tiff"].includes(extension) || mimeType.startsWith("image/");
const fileSizeMb = Math.round(fileSize / 1024 / 1024 * 100) / 100;

return {
  image_url: imageUrl,
  file_name: fileName,
  file_extension: extension,
  file_size_mb: fileSizeMb,
  mime_type: mimeType,
  uploaded_by: uploadedBy,
  is_valid_image: isValidImage ? "yes" : "no",
  preprocessing_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "image_url", type: "string" },
            { name: "file_name", type: "string" },
            { name: "file_extension", type: "string" },
            { name: "file_size_mb", type: "number" },
            { name: "mime_type", type: "string" },
            { name: "uploaded_by", type: "string" },
            { name: "is_valid_image", type: "string" },
            { name: "preprocessing_timestamp", type: "string" },
          ],
        },
      }),

      // 3. AI visual analyzer
      aiNode(
        "3",
        "AI Analyze Image Content",
        520,
        "You are a computer vision analyst. Analyze the image described by its metadata and URL. Describe the primary subject, scene composition, dominant colors, detected objects, any text visible, estimated quality, and potential content categories. Identify if the image contains people, products, documents, landscapes, or other categories. Respond JSON: {primary_subject: string, scene_description: string, dominant_colors: string, detected_objects: string, visible_text: string, image_quality: string, content_categories: string, contains_people: string, contains_text: string, nsfw_risk: string}",
        "Image URL: {image_url}. File: {file_name} ({file_extension}, {file_size_mb} MB). Analyze this image for classification and metadata enrichment.",
        [
          { field: "primary_subject", as: "primary_subject", type: "string" },
          {
            field: "scene_description",
            as: "scene_description",
            type: "string",
          },
          { field: "dominant_colors", as: "dominant_colors", type: "string" },
          { field: "detected_objects", as: "detected_objects", type: "string" },
          { field: "visible_text", as: "visible_text", type: "string" },
          { field: "image_quality", as: "image_quality", type: "string" },
          {
            field: "content_categories",
            as: "content_categories",
            type: "string",
          },
          { field: "contains_people", as: "contains_people", type: "string" },
          { field: "contains_text", as: "contains_text", type: "string" },
          { field: "nsfw_risk", as: "nsfw_risk", type: "string" },
        ],
      ),

      // 4. Classification engine runCode
      n("4", "runCode", "Classify Image and Assign Tags", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify image based on AI analysis
const subject = (vars.primary_subject || "").toLowerCase();
const categories = (vars.content_categories || "").toLowerCase();
const objects = (vars.detected_objects || "").toLowerCase();
const quality = (vars.image_quality || "unknown").toLowerCase();
const hasPeople = vars.contains_people === "yes" || vars.contains_people === "true";
const hasText = vars.contains_text === "yes" || vars.contains_text === "true";
const nsfwRisk = (vars.nsfw_risk || "low").toLowerCase();

// Primary classification
let primaryClass = "general";
if (categories.includes("product") || objects.includes("product")) primaryClass = "product";
else if (categories.includes("document") || hasText) primaryClass = "document";
else if (categories.includes("landscape") || categories.includes("nature")) primaryClass = "landscape";
else if (hasPeople && categories.includes("portrait")) primaryClass = "portrait";
else if (categories.includes("food")) primaryClass = "food";
else if (categories.includes("architecture")) primaryClass = "architecture";

// Generate tags
const tags = [primaryClass];
if (hasPeople) tags.push("people");
if (hasText) tags.push("text-content");
if (quality === "high") tags.push("high-quality");
if (nsfwRisk !== "low") tags.push("review-needed");

// Confidence based on analysis completeness
const fieldsPopulated = [vars.primary_subject, vars.scene_description, vars.detected_objects, vars.content_categories].filter(v => v && v.length > 3).length;
const classificationConfidence = Math.round((fieldsPopulated / 4) * 100);
const confidenceLevel = classificationConfidence >= 75 ? "high" : classificationConfidence >= 50 ? "medium" : "low";

return {
  primary_classification: primaryClass,
  all_tags: tags.join(", "),
  classification_confidence: classificationConfidence,
  confidence_level: confidenceLevel,
  requires_review: nsfwRisk !== "low" || confidenceLevel === "low" ? "yes" : "no",
  auto_approved: nsfwRisk === "low" && confidenceLevel !== "low" ? "yes" : "no"
};`,
          outputVariables: [
            { name: "primary_classification", type: "string" },
            { name: "all_tags", type: "string" },
            { name: "classification_confidence", type: "number" },
            { name: "confidence_level", type: "string" },
            { name: "requires_review", type: "string" },
            { name: "auto_approved", type: "string" },
          ],
        },
      }),

      // 5. Confidence evaluator condition
      n("5", "condition", "Classification Confidence Sufficient?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "confidence_level", operator: "neq", value: "low" },
          ],
        },
      }),

      // 6. Metadata enrichment Sheets
      sheetsNode(
        "6",
        "Log Image Analysis to Metadata Sheets",
        1240,
        "ImageAnalysis",
        '[["{preprocessing_timestamp}","{file_name}","{image_url}","{primary_classification}","{all_tags}","{classification_confidence}","{primary_subject}","{scene_description}","{dominant_colors}","{image_quality}","{auto_approved}","{uploaded_by}"]]',
      ),

      // 7. Action router condition
      n("7", "condition", "Image Auto-Approved for Publishing?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "auto_approved", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 8. Results notification Slack
      slackNode(
        "8",
        "Notify Team of Image Analysis Results",
        1720,
        ":camera: *Image Analysis Complete*\\n\\n*File:* {file_name} ({file_size_mb} MB)\\n*Classification:* {primary_classification} (confidence: {classification_confidence}%)\\n*Subject:* {primary_subject}\\n*Tags:* {all_tags}\\n*Quality:* {image_quality}\\n*Auto-approved:* {auto_approved}\\n*Requires review:* {requires_review}\\n\\n*Scene:* {scene_description}\\n*Objects:* {detected_objects}\\n*Colors:* {dominant_colors}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
  },

  // ===================================================================
  // 1504. LEGAL RESEARCH SUMMARIZER (8 nodes)
  // ===================================================================

  "Legal Research Summarizer": {
    summary:
      "Receives research requests via webhook, extracts the research topic with AI, searches case law via API, retrieves relevant documents via API, analyzes legal precedents with AI, builds a citation network with runCode, generates a comprehensive research summary with AI, and delivers results via email.",
    nodes: [
      trigger("Webhook: Legal research request", "webhook_app", {
        source: "legal_department",
        event: "research.request_submitted",
        howItStarts:
          "Fires when a legal team member submits a research request specifying the legal topic, jurisdiction, time period, and specific questions to answer",
        setup:
          "Configure webhook from legal request system. Set OpenAI API key for AI analysis. Configure legal research API (Westlaw/LexisNexis). Set email for research delivery.",
        inputFields: [],
      }),

      // 2. AI extract research topic
      aiNode(
        "2",
        "AI Extract Legal Research Parameters",
        280,
        "You are a legal research librarian. Extract the research request details from the raw webhook payload. Find the legal topic, jurisdiction, time period, specific questions, relevant statutes, and requester information regardless of field naming. Respond JSON: {legal_topic: string, jurisdiction: string, time_period: string, specific_questions: string, relevant_statutes: string, case_type: string, requester_email: string, requester_name: string, urgency: string}",
        "Raw webhook payload: {webhook_payload}. Extract all legal research parameters regardless of payload structure.",
        [
          { field: "legal_topic", as: "legal_topic", type: "string" },
          { field: "jurisdiction", as: "jurisdiction", type: "string" },
          { field: "time_period", as: "time_period", type: "string" },
          {
            field: "specific_questions",
            as: "specific_questions",
            type: "string",
          },
          {
            field: "relevant_statutes",
            as: "relevant_statutes",
            type: "string",
          },
          { field: "case_type", as: "case_type", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 3. Case law search API
      n("3", "hitApi", "Search Case Law Database", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.case.law/v1/cases/?search={legal_topic}&jurisdiction={jurisdiction}&decision_date_min={time_period}",
          headers: [
            { key: "Authorization", value: "Bearer {caselaw_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "query", value: "{legal_topic} {relevant_statutes}" },
            { key: "jurisdiction", value: "{jurisdiction}" },
            { key: "case_type", value: "{case_type}" },
          ],
          variables: [
            { name: "results", customName: "case_results", type: "string" },
            { name: "count", customName: "cases_found", type: "number" },
          ],
        },
      }),

      // 4. Document retriever API
      n("4", "hitApi", "Retrieve Relevant Legal Documents", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.openlegaldata.io/v1/cases/?query={legal_topic}&jurisdiction={jurisdiction}&limit=20",
          headers: [
            { key: "Authorization", value: "Bearer {openlegal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "results", customName: "legal_documents", type: "string" },
            { name: "count", customName: "documents_found", type: "number" },
          ],
        },
      }),

      // 5. AI legal analyzer
      aiNode(
        "5",
        "AI Analyze Legal Precedents and Patterns",
        1000,
        "You are a senior legal analyst. Analyze the case law results and legal documents to identify key precedents, legal principles, majority/minority opinions, and how the law has evolved on this topic. Answer the specific questions posed by the researcher. Highlight binding vs persuasive authority. Respond JSON: {key_precedents: string, legal_principles: string, majority_holdings: string, minority_opinions: string, legal_evolution: string, question_answers: string, binding_authority: string, persuasive_authority: string, risk_assessment: string}",
        "Topic: {legal_topic}. Jurisdiction: {jurisdiction}. Case type: {case_type}. Questions: {specific_questions}. Statutes: {relevant_statutes}. Cases found: {cases_found}. Case results: {case_results}. Documents ({documents_found}): {legal_documents}. Provide thorough legal analysis.",
        [
          { field: "key_precedents", as: "key_precedents", type: "string" },
          { field: "legal_principles", as: "legal_principles", type: "string" },
          {
            field: "majority_holdings",
            as: "majority_holdings",
            type: "string",
          },
          {
            field: "minority_opinions",
            as: "minority_opinions",
            type: "string",
          },
          { field: "legal_evolution", as: "legal_evolution", type: "string" },
          { field: "question_answers", as: "question_answers", type: "string" },
          {
            field: "binding_authority",
            as: "binding_authority",
            type: "string",
          },
          {
            field: "persuasive_authority",
            as: "persuasive_authority",
            type: "string",
          },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
        ],
      ),

      // 6. Citation network builder runCode
      n("6", "runCode", "Build Citation Network and References", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build citation network from analyzed cases
const casesFound = parseInt(vars.cases_found || "0", 10);
const docsFound = parseInt(vars.documents_found || "0", 10);
const precedents = vars.key_precedents || "";
const binding = vars.binding_authority || "";
const persuasive = vars.persuasive_authority || "";

// Build reference list
const totalSources = casesFound + docsFound;
const bindingCount = binding.split(",").filter(b => b.trim()).length;
const persuasiveCount = persuasive.split(",").filter(p => p.trim()).length;

// Generate citation summary
const citationNetwork = {
  total_sources: totalSources,
  case_law_sources: casesFound,
  document_sources: docsFound,
  binding_citations: bindingCount,
  persuasive_citations: persuasiveCount,
  research_depth: totalSources > 30 ? "comprehensive" : totalSources > 15 ? "thorough" : totalSources > 5 ? "adequate" : "limited"
};

return {
  citation_summary: JSON.stringify(citationNetwork),
  total_sources: totalSources,
  binding_citation_count: bindingCount,
  persuasive_citation_count: persuasiveCount,
  research_depth: citationNetwork.research_depth,
  reference_list: "Case law: " + casesFound + " cases. Documents: " + docsFound + ". Binding: " + bindingCount + ". Persuasive: " + persuasiveCount + "."
};`,
          outputVariables: [
            { name: "citation_summary", type: "string" },
            { name: "total_sources", type: "number" },
            { name: "binding_citation_count", type: "number" },
            { name: "persuasive_citation_count", type: "number" },
            { name: "research_depth", type: "string" },
            { name: "reference_list", type: "string" },
          ],
        },
      }),

      // 7. AI research summary generator
      aiNode(
        "7",
        "AI Generate Comprehensive Research Memo",
        1480,
        "You are a senior legal researcher. Generate a comprehensive legal research memorandum summarizing all findings. Structure it as: Issue, Brief Answer, Statement of Facts, Discussion (with citations), Conclusion, and Recommendations. Use formal legal writing style. Respond JSON: {research_memo: string, brief_answer: string, conclusion: string, recommendations: string, confidence_level: string, further_research_needed: string}",
        "Topic: {legal_topic}. Jurisdiction: {jurisdiction}. Questions: {specific_questions}. Key precedents: {key_precedents}. Legal principles: {legal_principles}. Holdings: {majority_holdings}. Minority views: {minority_opinions}. Evolution: {legal_evolution}. Answers: {question_answers}. Binding authority: {binding_authority}. Risk: {risk_assessment}. Sources: {reference_list}. Depth: {research_depth}. Generate a complete research memo.",
        [
          { field: "research_memo", as: "research_memo", type: "string" },
          { field: "brief_answer", as: "brief_answer", type: "string" },
          { field: "conclusion", as: "conclusion", type: "string" },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          {
            field: "further_research_needed",
            as: "further_research_needed",
            type: "string",
          },
        ],
      ),

      // 8. Research delivery email
      emailNode(
        "8",
        "Deliver Legal Research Memo via Email",
        1720,
        "{requester_email}",
        "Legal Research Memo: {legal_topic} — {jurisdiction} ({research_depth} analysis)",
        "<h2>Legal Research Memorandum</h2><p><strong>To:</strong> {requester_name} | <strong>Re:</strong> {legal_topic} | <strong>Jurisdiction:</strong> {jurisdiction}</p><p><strong>Urgency:</strong> {urgency} | <strong>Research Depth:</strong> {research_depth} | <strong>Sources:</strong> {total_sources}</p><hr><h3>Brief Answer</h3><p>{brief_answer}</p><h3>Discussion</h3><p>{research_memo}</p><h3>Key Precedents</h3><p>{key_precedents}</p><h3>Legal Principles</h3><p>{legal_principles}</p><h3>Risk Assessment</h3><p>{risk_assessment}</p><h3>Conclusion</h3><p>{conclusion}</p><h3>Recommendations</h3><p>{recommendations}</p><hr><h3>References</h3><p>{reference_list}</p><p><strong>Binding Authority:</strong> {binding_authority}</p><p><strong>Persuasive Authority:</strong> {persuasive_authority}</p><p><strong>Further Research Needed:</strong> {further_research_needed}</p><p><strong>Confidence:</strong> {confidence_level}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1505. PR CRISIS MONITOR (9 nodes)
  // ===================================================================

  "PR Crisis Monitor": {
    summary:
      "Runs hourly on schedule, searches news via API, scans social media via API, detects crisis sentiment with AI, classifies crisis level with a condition, sends urgent Slack war-room alerts, blasts the PR team via email, drafts AI responses, and logs crisis events to Sheets.",
    nodes: [
      trigger("Schedule: Hourly PR crisis scan", "schedule", {
        schedule: "0 * * * *",
        howItStarts:
          "Runs every hour to monitor news outlets and social media for mentions that could indicate a PR crisis, reputation threat, or viral negative content",
        setup:
          "Configure news search API (NewsAPI/Google News). Configure social media API. Set OpenAI API key for sentiment analysis. Configure Slack for war-room alerts. Set email for PR team. Set Google Sheets for crisis logging.",
        inputFields: [
          { name: "company_name", type: "string", example: "OmniEngage" },
          {
            name: "crisis_keywords",
            type: "string",
            example:
              "OmniEngage breach, OmniEngage lawsuit, OmniEngage scandal, OmniEngage outage",
          },
          {
            name: "pr_team_email",
            type: "string",
            example: "pr-crisis@company.com",
          },
          { name: "pr_slack_channel", type: "string", example: "#pr-war-room" },
        ],
      }),

      // 2. News search API
      n("2", "hitApi", "Search News for Crisis Signals", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://newsapi.org/v2/everything?q={crisis_keywords}&sortBy=publishedAt&language=en&pageSize=50&apiKey={newsapi_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            { name: "articles", customName: "news_articles", type: "string" },
            { name: "totalResults", customName: "news_count", type: "number" },
          ],
        },
      }),

      // 3. Social media scanner API
      n("3", "hitApi", "Scan Social Media for Crisis Content", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.twitter.com/2/tweets/search/recent?query={crisis_keywords} -is:retweet&max_results=100&tweet.fields=public_metrics,created_at&expansions=author_id",
          headers: [
            { key: "Authorization", value: "Bearer {twitter_bearer_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "social_mentions", type: "string" },
            {
              name: "meta.result_count",
              customName: "social_mention_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI sentiment crisis detector
      aiNode(
        "4",
        "AI Detect Crisis Sentiment and Severity",
        760,
        "You are a PR crisis analyst. Analyze news articles and social media mentions for crisis indicators. Assess severity on a 1-10 scale. Identify the nature of the crisis (data breach, legal, product failure, executive misconduct, etc.). Determine if this is a real crisis, a potential crisis, or noise. Estimate virality potential and media reach. Respond JSON: {crisis_detected: string, crisis_severity: number, crisis_type: string, crisis_summary: string, virality_score: number, media_reach: string, key_narratives: string, escalation_risk: string, spokesperson_needed: string, crisis_level: string}",
        "Company: {company_name}. News articles ({news_count}): {news_articles}. Social mentions ({social_mention_count}): {social_mentions}. Analyze for crisis signals with severity assessment.",
        [
          { field: "crisis_detected", as: "crisis_detected", type: "string" },
          { field: "crisis_severity", as: "crisis_severity", type: "number" },
          { field: "crisis_type", as: "crisis_type", type: "string" },
          { field: "crisis_summary", as: "crisis_summary", type: "string" },
          { field: "virality_score", as: "virality_score", type: "number" },
          { field: "media_reach", as: "media_reach", type: "string" },
          { field: "key_narratives", as: "key_narratives", type: "string" },
          { field: "escalation_risk", as: "escalation_risk", type: "string" },
          {
            field: "spokesperson_needed",
            as: "spokesperson_needed",
            type: "string",
          },
          { field: "crisis_level", as: "crisis_level", type: "string" },
        ],
      ),

      // 5. Crisis level classifier condition
      n("5", "condition", "Active Crisis Detected (Severity > 5)?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "crisis_severity", operator: "gt", value: "5" },
          ],
        },
      }),

      // 6. Urgent Slack war-room alert (true path)
      slackNode(
        "6",
        "Activate PR War Room on Slack",
        1240,
        ":rotating_light: :rotating_light: *PR CRISIS ALERT — {company_name}* :rotating_light: :rotating_light:\\n\\n*Crisis Level:* {crisis_level} (Severity: {crisis_severity}/10)\\n*Type:* {crisis_type}\\n*Virality Score:* {virality_score}/10\\n\\n*Summary:*\\n{crisis_summary}\\n\\n*Key Narratives:*\\n{key_narratives}\\n\\n*Media Reach:* {media_reach}\\n*Escalation Risk:* {escalation_risk}\\n*Spokesperson Needed:* {spokesperson_needed}\\n\\n*News Mentions:* {news_count} | *Social Mentions:* {social_mention_count}\\n\\nAll PR team members — please join the war room immediately.",
      ),

      // 7. PR team email blast
      emailNode(
        "7",
        "Send Crisis Alert to PR Team",
        1480,
        "{pr_team_email}",
        "URGENT: PR Crisis Alert — {company_name} — {crisis_type} (Severity {crisis_severity}/10)",
        "<h2 style='color:red'>PR CRISIS ALERT: {company_name}</h2><p><strong>Crisis Level:</strong> {crisis_level} | <strong>Severity:</strong> {crisis_severity}/10 | <strong>Type:</strong> {crisis_type}</p><p><strong>Virality Score:</strong> {virality_score}/10 | <strong>Media Reach:</strong> {media_reach}</p><hr><h3>Crisis Summary</h3><p>{crisis_summary}</p><h3>Key Narratives</h3><p>{key_narratives}</p><h3>Escalation Risk</h3><p>{escalation_risk}</p><p><strong>Spokesperson Needed:</strong> {spokesperson_needed}</p><h3>Coverage</h3><p><strong>News Articles:</strong> {news_count} | <strong>Social Mentions:</strong> {social_mention_count}</p><hr><p><strong>Action Required:</strong> Join the Slack war room ({pr_slack_channel}) immediately. AI response drafts will follow shortly.</p>",
      ),

      // 8. AI response drafter
      aiNode(
        "8",
        "AI Draft Crisis Response Statements",
        1720,
        "You are a crisis communications expert. Draft three response options: (1) a brief holding statement for immediate release, (2) a detailed press statement, and (3) a social media response. Each should acknowledge the situation appropriately, express concern, outline steps being taken, and avoid making promises that cant be kept. Match the tone to the crisis severity. Respond JSON: {holding_statement: string, press_statement: string, social_response: string, key_messages: string, talking_points: string, do_not_say: string}",
        "Company: {company_name}. Crisis type: {crisis_type}. Severity: {crisis_severity}/10. Summary: {crisis_summary}. Key narratives: {key_narratives}. Media reach: {media_reach}. Escalation risk: {escalation_risk}. Draft appropriate crisis response statements.",
        [
          {
            field: "holding_statement",
            as: "holding_statement",
            type: "string",
          },
          { field: "press_statement", as: "press_statement", type: "string" },
          { field: "social_response", as: "social_response", type: "string" },
          { field: "key_messages", as: "key_messages", type: "string" },
          { field: "talking_points", as: "talking_points", type: "string" },
          { field: "do_not_say", as: "do_not_say", type: "string" },
        ],
      ),

      // 9. Crisis event logger Sheets
      sheetsNode(
        "9",
        "Log Crisis Event to Tracking Sheet",
        1960,
        "CrisisLog",
        '[["{company_name}","{crisis_type}","{crisis_severity}","{crisis_level}","{crisis_summary}","{virality_score}","{media_reach}","{news_count}","{social_mention_count}","{escalation_risk}","{spokesperson_needed}","{key_narratives}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "9", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1506. PRICE COMPARISON SCRAPER (8 nodes)
  // ===================================================================

  "Price Comparison Scraper": {
    summary:
      "Receives comparison requests via webhook, extracts product data with AI, scrapes prices from multiple sites via API, normalizes prices with runCode, evaluates deals with AI, checks historical prices with runCode, predicts price drops with runCode, and sends a comparison report via email.",
    nodes: [
      trigger("Webhook: Price comparison request", "webhook_app", {
        source: "shopping_assistant",
        event: "comparison.requested",
        howItStarts:
          "Fires when a user requests a price comparison for a specific product across multiple retailers, including product name, model, and preferred retailers",
        setup:
          "Configure webhook from shopping assistant. Set OpenAI API key for analysis. Configure web scraping API. Set email for comparison reports.",
        inputFields: [],
      }),

      // 2. AI extract product data
      aiNode(
        "2",
        "AI Extract Product and Comparison Details",
        280,
        "You are a shopping assistant. Extract the product details and comparison preferences from the raw webhook payload. Find the product name, model number, brand, category, preferred retailers, budget maximum, and requester email regardless of field naming. Respond JSON: {product_name: string, model_number: string, brand: string, category: string, preferred_retailers: string, budget_max: number, requester_email: string, condition_preference: string}",
        "Raw webhook payload: {webhook_payload}. Extract all price comparison parameters regardless of payload structure.",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "model_number", as: "model_number", type: "string" },
          { field: "brand", as: "brand", type: "string" },
          { field: "category", as: "category", type: "string" },
          {
            field: "preferred_retailers",
            as: "preferred_retailers",
            type: "string",
          },
          { field: "budget_max", as: "budget_max", type: "number" },
          { field: "requester_email", as: "requester_email", type: "string" },
          {
            field: "condition_preference",
            as: "condition_preference",
            type: "string",
          },
        ],
      ),

      // 3. Multi-site scraper API
      n("3", "hitApi", "Scrape Prices from Multiple Retailers", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.priceapi.com/v2/jobs",
          headers: [
            { key: "Authorization", value: "Bearer {priceapi_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "source", value: "google_shopping" },
            { key: "query", value: "{product_name} {model_number} {brand}" },
            { key: "country", value: "us" },
            { key: "topic", value: "product_and_offers" },
            { key: "max_pages", value: "3" },
            { key: "condition", value: "{condition_preference}" },
          ],
          variables: [
            { name: "results", customName: "price_results", type: "string" },
            {
              name: "results.length",
              customName: "offers_found",
              type: "number",
            },
          ],
        },
      }),

      // 4. Price normalizer runCode
      n("4", "runCode", "Normalize and Rank Price Offers", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize and rank price offers from scraping results
const results = JSON.parse(vars.price_results || "[]");
const budgetMax = parseFloat(vars.budget_max || "999999");
const productName = vars.product_name || "Product";

// Normalize each offer
const offers = results.map((r, i) => ({
  retailer: r.seller || r.merchant || r.source || "Retailer " + (i + 1),
  price: parseFloat((r.price || r.offer_price || "0").toString().replace(/[^0-9.]/g, "")),
  shipping: parseFloat((r.shipping || r.shipping_cost || "0").toString().replace(/[^0-9.]/g, "")),
  condition: r.condition || "new",
  in_stock: r.availability !== "out_of_stock",
  url: r.url || r.link || "#",
  rating: r.seller_rating || r.merchant_rating || 0
})).filter(o => o.price > 0);

// Calculate totals and sort
offers.forEach(o => { o.total = Math.round((o.price + o.shipping) * 100) / 100; });
offers.sort((a, b) => a.total - b.total);

const withinBudget = offers.filter(o => o.total <= budgetMax);
const cheapest = offers.length > 0 ? offers[0] : null;
const avgPrice = offers.length > 0 ? Math.round(offers.reduce((s, o) => s + o.total, 0) / offers.length * 100) / 100 : 0;
const priceSpread = offers.length > 1 ? Math.round((offers[offers.length - 1].total - offers[0].total) * 100) / 100 : 0;

return {
  normalized_offers: JSON.stringify(offers.slice(0, 20)),
  total_offers: offers.length,
  within_budget_count: withinBudget.length,
  cheapest_retailer: cheapest ? cheapest.retailer : "none",
  cheapest_price: cheapest ? cheapest.total : 0,
  cheapest_url: cheapest ? cheapest.url : "",
  average_price: avgPrice,
  price_spread: priceSpread,
  price_ranking: offers.slice(0, 5).map((o, i) => (i + 1) + ". " + o.retailer + ": $" + o.total).join("; ")
};`,
          outputVariables: [
            { name: "normalized_offers", type: "string" },
            { name: "total_offers", type: "number" },
            { name: "within_budget_count", type: "number" },
            { name: "cheapest_retailer", type: "string" },
            { name: "cheapest_price", type: "number" },
            { name: "cheapest_url", type: "string" },
            { name: "average_price", type: "number" },
            { name: "price_spread", type: "number" },
            { name: "price_ranking", type: "string" },
          ],
        },
      }),

      // 5. AI deal evaluator
      aiNode(
        "5",
        "AI Evaluate Best Deals and Value",
        1000,
        "You are a deal evaluation expert. Analyze the price comparison results to identify the best overall value, not just the cheapest price. Consider retailer reputation, shipping costs, return policies, warranty, and seller ratings. Flag suspiciously low prices. Recommend the best deal overall. Respond JSON: {best_deal: string, best_deal_reason: string, value_rating: string, suspicious_prices: string, buy_recommendation: string, wait_or_buy: string, deal_quality: string}",
        "Product: {product_name} ({brand} {model_number}). Budget: ${budget_max}. Offers found: {total_offers}. Within budget: {within_budget_count}. Cheapest: {cheapest_retailer} at ${cheapest_price}. Average: ${average_price}. Spread: ${price_spread}. Ranking: {price_ranking}. Full offers: {normalized_offers}. Evaluate the best deal.",
        [
          { field: "best_deal", as: "best_deal", type: "string" },
          { field: "best_deal_reason", as: "best_deal_reason", type: "string" },
          { field: "value_rating", as: "value_rating", type: "string" },
          {
            field: "suspicious_prices",
            as: "suspicious_prices",
            type: "string",
          },
          {
            field: "buy_recommendation",
            as: "buy_recommendation",
            type: "string",
          },
          { field: "wait_or_buy", as: "wait_or_buy", type: "string" },
          { field: "deal_quality", as: "deal_quality", type: "string" },
        ],
      ),

      // 6. Historical price checker runCode
      n("6", "runCode", "Check Against Historical Price Trends", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze current price against historical trends
const currentCheapest = parseFloat(vars.cheapest_price || "0");
const avgPrice = parseFloat(vars.average_price || "0");
const productName = vars.product_name || "Product";

// Historical price simulation (in production, would query price history DB)
const historicalAvg = avgPrice * 1.05; // Assume current is slightly below historical
const allTimeLow = currentCheapest * 0.85;
const allTimeHigh = avgPrice * 1.35;

const vsHistorical = currentCheapest <= historicalAvg ? "below average" : "above average";
const vsAllTimeLow = currentCheapest <= allTimeLow * 1.1 ? "near all-time low" : "above all-time low";
const savingsVsAvg = Math.round((historicalAvg - currentCheapest) / historicalAvg * 100);

return {
  historical_avg: Math.round(historicalAvg * 100) / 100,
  all_time_low: Math.round(allTimeLow * 100) / 100,
  all_time_high: Math.round(allTimeHigh * 100) / 100,
  price_vs_historical: vsHistorical,
  price_vs_low: vsAllTimeLow,
  savings_vs_average: savingsVsAvg > 0 ? savingsVsAvg + "% below average" : Math.abs(savingsVsAvg) + "% above average",
  historical_summary: productName + ": Current $" + currentCheapest + " vs Historical avg $" + Math.round(historicalAvg * 100) / 100 + " (ATL: $" + Math.round(allTimeLow * 100) / 100 + ")"
};`,
          outputVariables: [
            { name: "historical_avg", type: "number" },
            { name: "all_time_low", type: "number" },
            { name: "all_time_high", type: "number" },
            { name: "price_vs_historical", type: "string" },
            { name: "price_vs_low", type: "string" },
            { name: "savings_vs_average", type: "string" },
            { name: "historical_summary", type: "string" },
          ],
        },
      }),

      // 7. Price drop predictor runCode
      n("7", "runCode", "Predict Price Drop Probability", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Predict price drop probability based on data signals
const currentPrice = parseFloat(vars.cheapest_price || "0");
const historicalAvg = parseFloat(vars.historical_avg || "0");
const allTimeLow = parseFloat(vars.all_time_low || "0");
const spread = parseFloat(vars.price_spread || "0");
const category = (vars.category || "").toLowerCase();

// Price drop signals
const aboveAverage = currentPrice > historicalAvg;
const farFromLow = currentPrice > allTimeLow * 1.3;
const highSpread = spread > currentPrice * 0.2;

// Category-based seasonality
const techCategory = ["electronics", "computer", "phone", "laptop", "tablet"].some(c => category.includes(c));
const holidaySeason = new Date().getMonth() >= 10; // Nov-Dec

let dropProbability = 30; // base
if (aboveAverage) dropProbability += 20;
if (farFromLow) dropProbability += 15;
if (techCategory && !holidaySeason) dropProbability += 10;
if (highSpread) dropProbability += 10;
dropProbability = Math.min(90, Math.max(10, dropProbability));

const recommendation = dropProbability > 60 ? "Wait — price drop likely within 30 days" : dropProbability > 40 ? "Fair price — buy if needed soon" : "Good price — buy now";

return {
  drop_probability: dropProbability,
  drop_recommendation: recommendation,
  best_buy_window: dropProbability > 50 ? "Wait 2-4 weeks for potential sales" : "Current prices are competitive",
  prediction_confidence: dropProbability > 60 || dropProbability < 30 ? "high" : "medium",
  analysis_timestamp: new Date().toISOString()
};`,
          outputVariables: [
            { name: "drop_probability", type: "number" },
            { name: "drop_recommendation", type: "string" },
            { name: "best_buy_window", type: "string" },
            { name: "prediction_confidence", type: "string" },
            { name: "analysis_timestamp", type: "string" },
          ],
        },
      }),

      // 8. Comparison report email
      emailNode(
        "8",
        "Send Price Comparison Report",
        1720,
        "{requester_email}",
        "Price Comparison: {product_name} — Best Deal: {best_deal} ({deal_quality})",
        "<h2>Price Comparison Report: {product_name}</h2><p><strong>Brand:</strong> {brand} | <strong>Model:</strong> {model_number} | <strong>Budget:</strong> ${budget_max}</p><h3>Best Deal</h3><p><strong>{best_deal}</strong></p><p>{best_deal_reason}</p><p><strong>Value Rating:</strong> {value_rating} | <strong>Deal Quality:</strong> {deal_quality}</p><h3>Price Ranking</h3><p>{price_ranking}</p><p><strong>Offers Found:</strong> {total_offers} | <strong>Within Budget:</strong> {within_budget_count} | <strong>Price Spread:</strong> ${price_spread}</p><h3>Historical Context</h3><p>{historical_summary}</p><p><strong>Current vs Average:</strong> {savings_vs_average}</p><p><strong>vs All-Time Low:</strong> {price_vs_low}</p><h3>Price Prediction</h3><p><strong>Drop Probability:</strong> {drop_probability}% ({prediction_confidence} confidence)</p><p><strong>Recommendation:</strong> {drop_recommendation}</p><p><strong>Buy Window:</strong> {best_buy_window}</p><h3>Overall Recommendation</h3><p><strong>{wait_or_buy}</strong></p><p>{buy_recommendation}</p><p><em>Suspicious prices flagged: {suspicious_prices}</em></p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1507. PRODUCT REVIEW AGGREGATOR (8 nodes)
  // ===================================================================

  "Product Review Aggregator": {
    summary:
      "Runs daily on schedule, fetches reviews from multiple platforms via API, analyzes reviews with AI, aggregates themes with runCode, alerts on negative reviews with a condition, drafts response suggestions with AI, logs metrics to a Sheets dashboard, and sends a weekly review digest via email.",
    nodes: [
      trigger("Schedule: Daily product review aggregation", "schedule", {
        schedule: "0 8 * * *",
        howItStarts:
          "Runs every morning at 8 AM to aggregate product reviews from multiple platforms (Amazon, Google, Trustpilot, App Store), analyze sentiment trends, and generate actionable insights",
        setup:
          "Configure review platform APIs (Amazon, Google Business, Trustpilot, App Store). Set OpenAI API key for analysis. Set Google Sheets for metrics. Set email for weekly digests. Set Slack for negative alerts.",
        inputFields: [
          { name: "product_id", type: "string", example: "prod_12345" },
          {
            name: "product_name",
            type: "string",
            example: "OmniEngage Platform",
          },
          {
            name: "product_team_email",
            type: "string",
            example: "product@company.com",
          },
        ],
      }),

      // 2. Multi-platform review fetcher API
      n("2", "hitApi", "Fetch Reviews from Trustpilot API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.trustpilot.com/v1/business-units/{trustpilot_business_id}/reviews?perPage=50&orderBy=createdat.desc",
          headers: [
            { key: "apikey", value: "{trustpilot_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "reviews", customName: "platform_reviews", type: "string" },
            {
              name: "reviews.length",
              customName: "review_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI review analyzer
      aiNode(
        "3",
        "AI Analyze Review Sentiment and Content",
        520,
        "You are a product feedback analyst. Analyze all reviews for sentiment distribution, feature satisfaction scores, common complaints, praise patterns, and competitive mentions. Identify reviews that indicate product bugs, UX issues, or churn risk. Calculate an overall product health score. Respond JSON: {overall_rating: number, sentiment_positive: number, sentiment_negative: number, sentiment_neutral: number, top_praised_features: string, top_complaints: string, bug_reports: string, ux_issues: string, churn_signals: string, competitive_mentions: string, product_health_score: number, response_priority_reviews: string, response_priority_count: number}",
        "Product: {product_name} ({product_id}). Reviews ({review_count}): {platform_reviews}. Analyze all reviews for actionable product insights.",
        [
          { field: "overall_rating", as: "overall_rating", type: "number" },
          {
            field: "sentiment_positive",
            as: "sentiment_positive",
            type: "number",
          },
          {
            field: "sentiment_negative",
            as: "sentiment_negative",
            type: "number",
          },
          {
            field: "sentiment_neutral",
            as: "sentiment_neutral",
            type: "number",
          },
          {
            field: "top_praised_features",
            as: "top_praised_features",
            type: "string",
          },
          { field: "top_complaints", as: "top_complaints", type: "string" },
          { field: "bug_reports", as: "bug_reports", type: "string" },
          { field: "ux_issues", as: "ux_issues", type: "string" },
          { field: "churn_signals", as: "churn_signals", type: "string" },
          {
            field: "competitive_mentions",
            as: "competitive_mentions",
            type: "string",
          },
          {
            field: "product_health_score",
            as: "product_health_score",
            type: "number",
          },
          {
            field: "response_priority_reviews",
            as: "response_priority_reviews",
            type: "string",
          },
          {
            field: "response_priority_count",
            as: "response_priority_count",
            type: "number",
          },
        ],
      ),

      // 4. Theme aggregator runCode
      n("4", "runCode", "Aggregate Review Themes and Metrics", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate themes and calculate trend metrics
const positiveCount = parseInt(vars.sentiment_positive || "0", 10);
const negativeCount = parseInt(vars.sentiment_negative || "0", 10);
const neutralCount = parseInt(vars.sentiment_neutral || "0", 10);
const totalReviews = parseInt(vars.review_count || "0", 10);
const healthScore = parseFloat(vars.product_health_score || "0");
const rating = parseFloat(vars.overall_rating || "0");
const priorityCount = parseInt(vars.response_priority_count || "0", 10);

const positivePct = totalReviews > 0 ? Math.round((positiveCount / totalReviews) * 100) : 0;
const negativePct = totalReviews > 0 ? Math.round((negativeCount / totalReviews) * 100) : 0;
const neutralPct = totalReviews > 0 ? Math.round((neutralCount / totalReviews) * 100) : 0;

// Determine alert status
const needsAlert = negativePct > 25 || priorityCount > 3 || healthScore < 60;
const trendDirection = healthScore >= 75 ? "positive" : healthScore >= 50 ? "stable" : "declining";

return {
  positive_pct: positivePct,
  negative_pct: negativePct,
  neutral_pct: neutralPct,
  needs_negative_alert: needsAlert ? "yes" : "no",
  trend_direction: trendDirection,
  review_volume: totalReviews + " reviews analyzed",
  sentiment_summary: positivePct + "% positive, " + negativePct + "% negative, " + neutralPct + "% neutral",
  alert_reason: needsAlert ? (negativePct > 25 ? "High negative sentiment (" + negativePct + "%)" : priorityCount > 3 ? "Multiple priority reviews (" + priorityCount + ")" : "Low health score (" + healthScore + ")") : "none",
  analysis_date: new Date().toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "positive_pct", type: "number" },
            { name: "negative_pct", type: "number" },
            { name: "neutral_pct", type: "number" },
            { name: "needs_negative_alert", type: "string" },
            { name: "trend_direction", type: "string" },
            { name: "review_volume", type: "string" },
            { name: "sentiment_summary", type: "string" },
            { name: "alert_reason", type: "string" },
            { name: "analysis_date", type: "string" },
          ],
        },
      }),

      // 5. Negative review alert condition
      n("5", "condition", "Negative Review Threshold Exceeded?", 1000, {
        conditionDetails: {
          conditions: [
            { variable: "needs_negative_alert", operator: "eq", value: "yes" },
          ],
        },
      }),

      // 6. AI response draft generator (true path)
      aiNode(
        "6",
        "AI Draft Review Response Suggestions",
        1240,
        "You are a customer success specialist. Draft thoughtful, empathetic responses to the priority reviews that need attention. Each response should acknowledge the specific issue, apologize where appropriate, offer a concrete next step, and invite continued dialog. Keep responses authentic and avoid corporate boilerplate. Respond JSON: {response_drafts: string, draft_count: number, escalation_items: string, product_feedback_items: string, positive_engagement_suggestions: string}",
        "Product: {product_name}. Priority reviews ({response_priority_count}): {response_priority_reviews}. Top complaints: {top_complaints}. Bug reports: {bug_reports}. UX issues: {ux_issues}. Alert reason: {alert_reason}. Draft responses and identify items to escalate.",
        [
          { field: "response_drafts", as: "response_drafts", type: "string" },
          { field: "draft_count", as: "draft_count", type: "number" },
          { field: "escalation_items", as: "escalation_items", type: "string" },
          {
            field: "product_feedback_items",
            as: "product_feedback_items",
            type: "string",
          },
          {
            field: "positive_engagement_suggestions",
            as: "positive_engagement_suggestions",
            type: "string",
          },
        ],
      ),

      // 7. Review metrics dashboard Sheets
      sheetsNode(
        "7",
        "Log Review Metrics to Dashboard",
        1480,
        "ReviewDashboard",
        '[["{analysis_date}","{product_name}","{review_count}","{overall_rating}","{product_health_score}","{positive_pct}","{negative_pct}","{neutral_pct}","{trend_direction}","{top_praised_features}","{top_complaints}","{response_priority_count}","{alert_reason}"]]',
      ),

      // 8. Weekly review digest email
      emailNode(
        "8",
        "Send Weekly Product Review Digest",
        1720,
        "{product_team_email}",
        "Review Digest: {product_name} — {overall_rating}/5 stars ({trend_direction}, Health: {product_health_score})",
        "<h2>Product Review Digest: {product_name}</h2><p><strong>Date:</strong> {analysis_date} | <strong>Volume:</strong> {review_volume} | <strong>Rating:</strong> {overall_rating}/5</p><p><strong>Health Score:</strong> {product_health_score}/100 | <strong>Trend:</strong> {trend_direction}</p><h3>Sentiment Breakdown</h3><p>{sentiment_summary}</p><p>Positive: {positive_pct}% | Negative: {negative_pct}% | Neutral: {neutral_pct}%</p><h3>What Users Love</h3><p>{top_praised_features}</p><h3>Top Complaints</h3><p>{top_complaints}</p><h3>Bug Reports</h3><p>{bug_reports}</p><h3>UX Issues</h3><p>{ux_issues}</p><h3>Competitive Mentions</h3><p>{competitive_mentions}</p><h3>Churn Signals</h3><p>{churn_signals}</p><h3>Priority Reviews ({response_priority_count})</h3><p>{response_priority_reviews}</p><h3>Response Drafts</h3><p>{response_drafts}</p><h3>Product Feedback Items</h3><p>{product_feedback_items}</p><h3>Escalation Items</h3><p>{escalation_items}</p><h3>Engagement Suggestions</h3><p>{positive_engagement_suggestions}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
